import classnames from "classnames";
import _ from "lodash";
import React, { Fragment, useContext, useEffect, useState } from "react";
import DebounceInput from "react-debounce-input";
import { FaEdit, FaRecycle, FaTimesCircle } from "react-icons/fa";
import { GrPrint } from "react-icons/gr";
import { toast } from "react-toastify";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import Switch from "react-switch";
import { Button, ButtonGroup, Card, CardBody, Col, Input, InputGroup, Label, Row, Table } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, rgx, UserContext, } from "../utils";
import { AttachmentManager, ConfirmModal } from "./";
import { useLocation, useParams } from "react-router-dom";

const HeaderColumn = ({ label, colWidth, underline }) => {
   return (
      <Col xs={colWidth} className="pt-2">
         <h5
            children={label}
            className={classnames(
               { "text-decoration-underline": underline === true, },
               "float-start fontSize92"
            )}
         />
      </Col>
   );
};

const travelerLabel = (
   <>
      Traveler Size<br />(Check all that apply)
   </>
);

const ELEMENT_TYPES = {
   DEBOUNCE: "debounceInput",
   SWITCH: "switch",
   MINMAX: "minMax",
   RADIO: "radio",
   SINGLE_SELECT: "single_select",
   MULTI_INPUT: "multi_input",
   TRAVELER_SIZE: "traveler_size",
   MULTI_BOOL: "multi_bool",
   MULTI_GRID: "multi_grid",
   HEADING: "heading",
};
const digiwind = "Digiwind";
const flyer = "Flyer";
const precision = "Precision";
const travelerErrorMessage = "Selected Traveler Sizes must be 1, 2 or 3";
const yarnTensioner = "Yarn Tensioner";
const bulletSizeOptions = [
   { label: 1, value: 1 },
   { label: 2, value: 2 },
   { label: 4, value: 4 },
   { label: 6, value: 6 },
];
const laborOptions = [
   { label: "25", value: 25 },
   { label: "50", value: 50 },
   { label: "75", value: 75 },
   { label: "100", value: 100 },
];
const onOffOptions = [
   { label: "On", value: true },
   { label: "Off", value: false },
];
const stopStartOptions = [
   { label: " Start", value: 1 },
   { label: " Stop", value: 0 },
];
const tensionDeviceOptions = [
   { label: flyer, value: flyer },
   { label: yarnTensioner, value: yarnTensioner },
];
const yesNoOptions = [
   { label: "Yes", value: true },
   { label: "No", value: false },
];
const commonElem = {
   APPLICATOR_ROLL_RPM: {
      label: "Applicator Roll RPM",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "applicatorRollRPM",
   },
   BACK_PRESS: {
      label: "Back Press (kg)",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.MINMAX,
      minField: "backPressMin",
      maxField: "backPressMax",
      maxLength: 4,
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 2, 0.0, 10.0, false, true),
      errorMessage: "Back Press must be 0.00 to 10.00.\n",
   },
   BACK_PRESSURE_BEGIN_END: {
      label: "Back Pressure",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "backPressureBegin",
      maxField: "backPressureEnd",
      maxLength: 5,
      disregardMinMax: true,
      minLabel: "Beginning",
      maxLabel: "End",
   },
   BACK_PRESSURE_SPRING: {
      label: "Back Pressure Spring",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.SINGLE_SELECT,
      field: "backPressureSpring",
      options: [
         { label: ".81 mm Brown", value: 1 },
         { label: ".91 mm Green", value: 2 },
         { label: "1.00 mm White", value: 3 },
         { label: "1.10 mm Purple", value: 4 },
         { label: "1.20 mm Red", value: 5 },
         { label: "1.40 mm Yellow", value: 6 },
      ],
      isRequired: () => { return true; },
   },
   BALLOON_GUIDE: {
      label: "Balloon Guide Height",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "balloonGuide",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9),
      errorMessage: "Beginning Tension must be 1 to 9.\n",
   },
   BEG_BACK_PRESSURE: {
      label: "Beginning Back Pressure (kg)",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.MINMAX,
      minField: "begBackPressureMin",
      maxField: "begBackPressureMax",
      maxLength: 3,
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 2, 0.0, 10.0, false, true),
      errorMessage: "Beginning Back Pressure must be 0.00 to 10.00.\n",
   },
   BEG_TENSION: {
      label: "Beginning Tension (g)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "begTensionMin",
      maxField: "begTensionMax",
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 600),
      errorMessage: "Beginning Tension must be 0 to 600.\n",
   },
   BOND_TEMP: {
      label: "Bond Temperature (+/-10°F)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "bondTemp",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 60, 250),
      errorMessage: "Bond Temperature must be 60 to 250.\n",
   },
   BOX_PRESSURE: {
      label: "Size Box Roll Pressure (psi)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "boxPressure",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
      errorMessage: "Size Box Roll Pressure must be 0 to 99.\n",
   },
   BOX_SETTING: {
      label: "Box Setting",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "boxSetting",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
      errorMessage: "Box Setting must be 0 to 99.\n",
   },
   BOX_STRETCH: {
      label: "Over Feed/Size Box Stretch (%) ",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "boxStretch",
   },
   BULLET_SIZE: {
      label: "Bullet Size",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.RADIO,
      options: bulletSizeOptions,
      field: "bullet",
      errorMessage: "Yarn Tensioner must also have a bullet size of 1, 2, 4, or 6.\n",
      validate: (value, field, item) => {
         // this only applies if the tension device is set to Yarn Tensioner
         if (item.tensionDevice === yarnTensioner) {
            return _.includes([1, 2, 4, 6], value);
         } else {
            return true;
         }
      },
      isRequired: (item) => { return item.tensionDevice === yarnTensioner; },
      subtitle: "Only applies to Yarn Tensioner",
      modify: (field, value, index, onChangeCallback, editItem) => {
         if (editItem["tensionDevice"] === yarnTensioner) {
            onChangeCallback(field, value, index);
         }
      },
   },
   CAPSTAN_WRAPS: {
      label: "Wraps On Capstan",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "capstanWraps",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 3),
      errorMessage: "Wraps On Capstan must be 1 to 3.\n",
   },
   CAPSTAN_WRAPS_SIX: {
      label: "Wraps On Capstan",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "capstanWraps",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 6),
      errorMessage: "Wraps On Capstan must be 1 to 6.\n",
   },
   CAPSTAN_WRAPS_SEVEN: {
      label: "Wraps On Capstan",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "capstanWraps",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 7),
      errorMessage: "Wraps On Capstan must be 1 to 7.\n",
   },
   CLOCK_SET: {
      label: "Clock setting (m) ",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "clockSet",
      dataType: constants.DATA_TYPES.INT,
      isRequired: () => { return true; },
      validate: (value, field) => rgx.validateIntegerWithCommas(value, field, 5, false, false),
      errorMessage: "Clock setting is required.\n",
   },
   CLOCK_SET_SIX: {
      label: "Clock setting (m) ",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "clockSet",
      dataType: constants.DATA_TYPES.INT,
      isRequired: () => { return true; },
      validate: (value, field) => rgx.validateIntegerWithCommas(value, field, 6, false, false),
      errorMessage: "Clock setting is required.\n",
   },
   CLOCK_SETTING: {
      label: "Clock setting (m) ",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "clockSetting",
      dataType: constants.DATA_TYPES.INT,
      isRequired: () => { return true; },
      validate: (value, field) => rgx.validateIntegerWithCommas(value, field, 5, false, false),
      errorMessage: "Clock setting is required.\n",
   },
   CLOCK_SETTING_SIX: {
      label: "Clock setting (m) ",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "clockSetting",
      dataType: constants.DATA_TYPES.INT,
      isRequired: () => { return true; },
      validate: (value, field) => rgx.validateIntegerWithCommas(value, field, 6, false, false),
      errorMessage: "Clock setting is required.\n",
   },
   COUNTER_SET: {
      label: "Counter Set (m)",
      field: "counterSet",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 99999),
      errorMessage: "Counter Set must be 1 to 99,999.\n",
   },
   COUNTER_SWITCH: {
      label: "Counter Switch",
      field: "counterSwitch",
      dataType: constants.DATA_TYPES.BOOL,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "Active", value: true },
         { label: "Closed", value: false },
      ],
      isRequired: () => { return true; },
      errorMessage: "Counter Switch is required\n",
   },
   CREEL_TENSION: {
      label: "Creel Tension (g)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "creelTensionMin",
      maxField: "creelTensionMax",
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 600),
      errorMessage: "Creel Tension must be 1 to 600.\n",
   },
   CREEL_TYPE: {
      label: "Creel Type",
      dataType: constants.DATA_TYPES.TEXT,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "Standard", value: "Standard" },
         { label: "Roll Off", value: "Roll Off" },
      ],
      maxLength: 10,
      isRequired: () => { return true; },
      field: "creelType",
      errorMessage: "Creel Type is required\n",
   },
   DANCER_ARM_TENSION: {
      label: "Dancer Arm Tension",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "dancerArmTension",
      validate: (value, field) => rgx.validateDecimalRangeIncremental(value, field, 2, 1, 1.0, 15.0, 0.5, false, false),
      errorMessage: "Dancer Arm Tension must be 1.0 to 15.0 in 0.5 increments.\n",
      isRequired: () => { return false; },
   },
   DECK_LOWER: {
      label: "Lower Deck (f=50Hz) (r/min)",
      field: "deckLower",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
   },
   DECK_UPPER: {
      label: "Upper Deck (f=50Hz) (r/min)",
      field: "deckUpper",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 2000, 6500),
      errorMessage: "Upper Deck must be 2,000 to 6,500.\n",
   },
   DEGREES: {
      label: "Degrees (°)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "degreesStart",
      maxField: "degreesEnd",
      maxLength: 2,
      minLabel: "Start",
      maxLabel: "End",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 16),
      errorMessage: "Degrees be 1° to 16°.\n",
   },
   DELAY_LOWER: {
      label: "Delayed Start for Lower (secs)",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "delayLower",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 15.0, false, true),
      errorMessage: "Delayed Start for Lower must be 0.0 to 15.0.\n",
   },
   DELAY_UPPER: {
      label: "Delayed Start for Upper (secs)",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "delayUpper",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 15.0, false, true),
      errorMessage: "Delayed Start for Upper must be 0.0 to 15.0.\n",
   },
   DELIVERY_SPEED: {
      label: "Delivery Speed (ypm)",
      type: ELEMENT_TYPES.MINMAX,
      dataType: constants.DATA_TYPES.SMALLINT,
      minField: "speedSlow",
      maxField: "speedFast",
      minLabel: "Slow",
      maxLabel: "Fast",
   },
   ENCODER_IMPULSE: {
      label: "Encoder Impulse (pulse/r)",
      field: "encoderImpulse",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 8, 200),
      errorMessage: "Encoder Impulse must be 8 to 200.\n",
   },
   END_BACK_PRESSURE: {
      label: "Ending Back Pressure (kg)",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.MINMAX,
      minField: "endBackPressureMin",
      maxField: "endBackPressureMax",
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 2, 0.0, 10.0, false, true),
      errorMessage: "Ending Back Pressure must be 0.00 to 10.00.\n",
   },
   F_ANTI: {
      label: "F(anti)",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "fAnti",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 20.0, false, true),
      errorMessage: "F(anti) must be 0.0 to 20.0.\n",
   },
   FAULT_WARNING: {
      label: "Fault Warning",
      field: "faultWarning",
      dataType: constants.DATA_TYPES.BOOL,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "Alarm Output", value: true },
         { label: "Auto Stop", value: false },
      ],
      isRequired: () => { return true; },
      errorMessage: "Fault Warning is required\n",
   },
   FEEDER_TENSION_SET: {
      label: "Feeder Tension Setting",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "feederTensionSet",
      dataType: constants.DATA_TYPES.SMALLINT,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 2, 995),
      errorMessage: "Feeder Tension Setting must be 2 to 995.\n",
   },
   FEEDER_WRAPS: {
      label: "Feeder Wraps",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "feederWraps",
      dataType: constants.DATA_TYPES.SMALLINT,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 3),
      errorMessage: "Feeder Wraps must be 1 to 3.\n",
   },
   FMIN_LOWER: {
      label: "Fmin Lower (Hz)",
      field: "fMinLower",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 20.0, false, true),
      errorMessage: "Fmin Lower must be 0.0 to 20.0.\n",
   },
   GEAR_A: {
      label: "Gear A",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "gearA",
   },
   GEAR_B: {
      label: "Gear B",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "gearB",
   },
   GEAR_C: {
      label: "Gear C",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "gearC",
   },
   GEAR_D: {
      label: "Gear D",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "gearD",
   },
   GEAR_E: {
      label: "Gear E",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "gearE",
   },
   GEAR_F: {
      label: "Gear F",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "gearF",
   },
   GRAF: {
      label: "Graf",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "graf",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 19.9, false, true),
      errorMessage: "Graf must be 0.00 to 19.9.\n",
      isRequired: () => { return true; },
   },
   H_ANTI: {
      label: "H(anti)",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "hAnti",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 20.0, false, true),
      errorMessage: "H(anti) must be 0.0 to 20.0.\n",
   },
   IMPULSE_SET: {
      label: "Impulse setting ",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "impulseSet",
      dataType: constants.DATA_TYPES.SMALLINT,
      isRequired: () => { return true; },
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 4700),
      errorMessage: "Impulse Setting must be 1 to 4,700.\n",
   },
   LABOR_PCT: {
      label: "Labor Assignment (%)",
      field: "laborPct",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.RADIO,
      options: laborOptions,
      isRequired: () => { return true; },
      validate: (value, field) =>
         _.some(laborOptions, (x) => {
            return x.value === value;
         }),
      errorMessage: "Labor Assignment must be entered.\n",
   },
   LAY_WHEEL_SET: {
      label: "Lay Wheel Setting",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "layWheelSet",
      dataType: constants.DATA_TYPES.FLOAT,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 38.0, false, true),
      errorMessage: "Lay Wheel Setting must be 0.00 to 38.00.\n",
   },
   LEFT_COMPENSATION: {
      label: "Left Compensation",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "leftCompensation",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
      errorMessage: "Left Compensation must be 0 to 99.\n",
   },
   LEFT_TAPER: {
      label: "Left Taper (°)",
      field: "leftTaper",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 1.0, 180.0, false, true),
      errorMessage: "Left Taper must be 1.0° to 180.0°.\n",
   },
   LUBE_ROLLER: {
      label: "Lube Roller",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "lubeRollerStart",
      maxField: "lubeRollerMax",
      disregardMinMax: true,
      minLabel: "Start",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 100),
      errorMessage: "Lube Roller must be 0 to 100.\n",
   },
   MACHINE_SETTING_1: {
      label: "Machine Setting 1",
      type: constants.DATA_TYPES.HEADING,
      isRequired: () => { return false; },
   },
   MACHINE_SETTING_2: {
      label: "Machine Setting 2",
      type: constants.DATA_TYPES.HEADING,
      isRequired: () => { return false; },
   },
   MACHINE_SETTING_3: {
      label: "Machine Setting 3",
      type: constants.DATA_TYPES.HEADING,
      isRequired: () => { return false; },
   },
   MULTI_SPEED: {
      label: "Speed (yds/min)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MULTI_INPUT,
   },
   NUMBER_OF_ENDS: {
      label: "Number of Ends (Cabled)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "numEnds",
   },
   NUMBER_PASSES: {
      label: "# Passes Top to Btm",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "1", value: 1 },
         { label: "3", value: 3 },
      ],
      isRequired: () => { return true; },
      field: "numberPasses",
   },
   OVEN_1_TEMP: {
      label: "#1 Oven Temp (°F) ",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "oven1TempTop",
      maxField: "oven1TempBtm",
      disregardMinMax: true,
      minLabel: "Top",
      maxLabel: "Bottom",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 60, 600),
      errorMessage: "Oven Temps must be 60 to 600.\n",
   },
   PACKAGE_TYPE: {
      label: "Package Type",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "packageType",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 4),
      errorMessage: "Package Type must be 1 to 4.\n",
   },
   PARAMETER_SETTING_1: {
      label: "Parameter Setting 1",
      type: constants.DATA_TYPES.HEADING,
      isRequired: () => { return false; },
   },
   PARAMETER_SETTING_2: {
      label: "Parameter Setting 2",
      type: constants.DATA_TYPES.HEADING,
      isRequired: () => { return false; },
   },
   PARAMETER_SETTING_3: {
      label: "Parameter Setting 3",
      type: constants.DATA_TYPES.HEADING,
      isRequired: () => { return false; },
   },
   POT_LIDS: {
      label: "Pot Lids",
      field: "potLids",
      dataType: constants.DATA_TYPES.BOOL,
      type: ELEMENT_TYPES.RADIO,
      options: yesNoOptions,
      validate: validateBoolean,
   },
   PREFEED_PRESSURE: {
      label: "Pre-Feeder Roll Pressure (psi)",
      field: "preFeedPressure",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
      errorMessage: "Pre-Feeder Roll Pressure must be 0 to 99.\n",
   },
   PREFEED_SETTING: {
      label: "Pre-Feeder Oscillator Setting",
      field: "preFeedSetting",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
   },
   PROGRAM_NAME: {
      label: "Program Name",
      field: "programName",
      dataType: constants.DATA_TYPES.TEXT,
      type: ELEMENT_TYPES.DEBOUNCE,
      maxLength: 50,
      isRequired: () => { return true; },
      errorMessage: "Program name is required.\n",
   },
   PROGRAM_NUMBER: {
      label: "Program #",
      field: "program",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
   },
   PROGRAM_TEXT: {
      label: "Program #",
      field: "program",
      dataType: constants.DATA_TYPES.TEXT,
      type: ELEMENT_TYPES.DEBOUNCE,
      isRequired: () => { return false; },
      errorMessage: "Program # must be entered.\n",
   },
   PULSE_RATE: {
      label: "Pulse Rate",
      field: "pulseRate",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "x1", value: 0 },
         { label: "x4", value: 1 },
      ],
      isRequired: () => { return true; },
      errorMessage: "Pulse Rate is required\n",
   },
   RECIPE: {
      label: "Recipe",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "recipe",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 50),
      errorMessage: "Recipe must be 1 to 50.\n",
      isRequired: () => { return false; },
   },
   RIGHT_COMPENSATION: {
      label: "Right Compensation",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "rightCompensation",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
      errorMessage: "Right Compensation must be 0 to 99.\n",
   },
   RIGHT_TAPER: {
      label: "Right Taper (°)",
      field: "rightTaper",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 1.0, 180.0, false, true),
      errorMessage: "Right Taper must be 1.0° to 180.0°.\n",
   },
   RING_SPEED_DOWN: {
      label: "Ring Down Speed (mm/s)",
      field: "ringSpeedDown",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 5.0, 99.9, false, true),
      errorMessage: "Ring Down Speed must be 5.0 to 99.9.\n",
   },
   RING_SPEED_UP: {
      label: "Ring Up Speed (mm/s)",
      field: "ringSpeedUp",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 5.0, 99.9, false, true),
      errorMessage: "Ring Up Speed must be 5.0 to 99.9.\n",
   },
   RING_START_POS: {
      label: "Ring Start Position (mm)",
      field: "ringStartPos",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 5.0, 50.0, false, true),
      errorMessage: "Ring Start Position must be 5.0 to 50.0.\n",
   },
   ROLLER_COEFFICIENT: {
      label: "Roller Coefficient",
      field: "rollerCoefficient",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 1500),
      errorMessage: "Roller Coefficient must be 0 to 1,500.\n",
   },
   ROLLER_DIAMETER: {
      label: "Roller Diameter (mm)",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "rollerDiameter",
      dataType: constants.DATA_TYPES.FLOAT,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 3, 1, 20.0, 200.0, false, true),
      errorMessage: "Roller Diameter must be 20.0 to 200.0.\n",
   },
   ROLLER_IMPULSE: {
      label: "Roller Impulse (pulse/r)",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "rollerImpulse",
      dataType: constants.DATA_TYPES.FLOAT,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 8, 200),
      errorMessage: "Roller Impulse must be 0 to 1,500.\n",
   },
   ROLLER_SPEED: {
      label: "Lube Roller Speed (rpm)",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "speed",
      dataType: constants.DATA_TYPES.INT,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 20.0, false, true),
      errorMessage: "Lube Roller Speed must be 0.0 to 20.0.\n",
   },
   RUN_RESET: {
      label: "Spindle Run when Reset",
      field: "runReset",
      dataType: constants.DATA_TYPES.BOOL,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "Off", value: false },
         { label: "On", value: true },
      ],
      isRequired: () => { return true; },
      errorMessage: "Spindle Run when Reset is required\n",
   },
   RUNTIME: {
      label: "Run Time (HH:MM:SS) ",
      field: "runTime",
      dataType: constants.DATA_TYPES.TEXT,
      maxLength: 10,
      isRequired: () => { return true; },
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateHMS(value, field),
      errorMessage: "Run Time must be in HH:MM:SS format\n",
   },
   SCREW_PITCH: {
      label: "Screw Pitch (mm)",
      field: "screwPitch",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 4.0, 20.0, false, true),
      errorMessage: "Screw Pitch must be 4.0 to 20.0.\n",
   },
   SPACING: {
      label: "Spacing",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "spacing",
      dataType: constants.DATA_TYPES.SMALLINT,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 8500),
      errorMessage: "Yarn Tensioner (LI) must be 0 to 8,500.\n",
   },
   SPECIFICATION: {
      label: "Specification",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "specification",
      dataType: constants.DATA_TYPES.TEXT,
      maxLength: 10,
      errorMessage: "Specification must be 999d9\n",
      validate: (value, field) => {
         let baseRegex = /^\d{3}d\d{1}$/;
         return baseRegex.test(value);
      },
   },
   SPEED: {
      label: "Speed setting (rpm)",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "speed",
      dataType: constants.DATA_TYPES.INT,
   },
   SPEED_LOWER: {
      label: "Lower Spindle Speed (rpm)",
      field: "speedLower",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1500, 8500),
      errorMessage: "Lower Spindle Speed must be 1,500 to 8,500.\n",
   },
   SPEED_MONITORING: {
      label: "Speed Monitoring",
      field: "speedMonitoring",
      dataType: constants.DATA_TYPES.BOOL,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "Closed", value: true },
         { label: "Active", value: false },
      ],
      isRequired: () => { return true; },
      errorMessage: "Speed Monitoring is required\n",
   },
   SPEED_SET: {
      label: "Speed setting",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "speedSet",
      dataType: constants.DATA_TYPES.SMALLINT,
   },
   SPEED_SETTING: {
      label: "Speed setting",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "speedSetting",
      dataType: constants.DATA_TYPES.SMALLINT,
   },
   SPEED_UPPER: {
      label: "Upper Spindle Speed (rpm)",
      field: "speedUpper",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
   },
   SPINDLE_DIRECTION: {
      label: "Spindle Direction",
      field: "rotation",
      dataType: constants.DATA_TYPES.BOOL,
      type: ELEMENT_TYPES.RADIO,
      options: [
         { label: "S", value: "S" },
         { label: "Z", value: "Z" },
      ],
      isRequired: () => { return true; },
      errorMessage: "Spindle Direction is required\n",
   },
   SPINDLE_SPEED: {
      label: "Spindle Speed (meters/min)",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "spindleSpeed",
      dataType: constants.DATA_TYPES.SMALLINT,
   },
   SPINDLE_WRAPS: {
      label: "Wraps on Spindle",
      field: "spindleWraps",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 2, 0.0, 3.0, false, true),
      errorMessage: "Wraps on Spindle must be 0.00 to 3.00.\n",
   },
   STD_EFFICIENCY: {
      label: "Std. Efficiency (%)",
      field: "stdEfficiency",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 100),
      errorMessage: "Std. Efficiency must be 1 to 100.\n",
   },
   STD_SPINDLE_COUNT: {
      label: "Std. Spindle Count",
      field: "stdSpindleCount",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999),
      errorMessage: "Std. Efficiency must be 0 to 999.\n",
   },
   STROKE_INCREMENT: {
      label: "Stroke Increment (mm)",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "strokeIncrement",
      dataType: constants.DATA_TYPES.FLOAT,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.1, 5.0, false, true),
      errorMessage: "Stroke Increment must be 0.1 to 5.0.\n",
   },
   STROKE_MAX: {
      label: "Max Stroke (mm)",
      field: "maxStroke",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 3, 2, 25.0, 370.0, false, true),
      errorMessage: "Max Stroke must be 25.0 to 370.0.\n",
   },
   STROKE_MAX_COUNT: {
      label: "No. of Max Stroke",
      field: "maxStrokeCount",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 3, 1, 1.0, 100.0, false, true),
      errorMessage: "No. of Max Stroke must be 1.0 to 100.0.\n",
   },
   STROKE_MAX_CYCLE: {
      label: "Max Stroke Cycle",
      field: "maxStrokeCycle",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 1.0, 20.0, false, true),
      errorMessage: "Max Stroke Cycle must be 1.0 to 20.0.\n",
   },
   STROKE_MIN: {
      label: "Min Stroke (mm)",
      field: "minStroke",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 3, 2, 25.0, 370.0, false, true),
      errorMessage: "Max Stroke must be 25.0 to 370.0.\n",
   },
   TENSION: {
      label: "Tension",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "tensionStart",
      maxField: "tensionEnd",
      disregardMinMax: true,
      minLabel: "Start",
      maxLabel: "End",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 100),
      errorMessage: "Tension must be 0 to 100.\n",
   },
   TENSION_BALL_SPINDLE: {
      label: "Tension Balls (Spindle)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "ballSpindle",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 10),
      errorMessage: "Tension Balls (Spindle) must be 0 to 10.\n",
   },
   TENSION_BALL_TOP: {
      label: "Tension Balls (Top)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "ballTop",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 10),
      errorMessage: "Tension Balls (Top) must be 0 to 10.\n",
   },
   TENSION_DEVICE: {
      label: "Tension Device",
      field: "tensionDevice",
      dataType: constants.DATA_TYPES.TEXT,
      type: ELEMENT_TYPES.RADIO,
      options: tensionDeviceOptions,
      isRequired: () => { return true; },
      maxLength: 20,
      errorMessage: "Tension Device is required\n",
      modify: (field, value, index, onChangeCallback, editItem, setSpecInfo, specInfo) => {
         let newList = specInfo.machineSetupList.slice();
         let changes = _.assign({}, newList[index]);
         changes[field] = value;
         if (value === flyer) {
            changes["bullet"] = null;
            changes["springNum"] = null;
         }
         newList[index] = changes;
         let newInfo = _.assign({}, specInfo);
         newInfo.machineSetupList = newList;
         setSpecInfo(newInfo);
      },
   },
   TENSION_POST_SET: {
      label: "Tension Post Setting",
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "tensionPostSet",
      dataType: constants.DATA_TYPES.FLOAT,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 9.0, false, true),
      errorMessage: "Tension Post Setting must be 0.00 to 9.00.\n",
   },
   TENSION_SET: {
      label: "Tension Setting",
      field: "tensionSet",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 9.0, false, true),
      errorMessage: "Tension Setting must be 0.0 to 9.0.\n",
   },
   THREAD_BREAK_TIMEOUT: {
      label: "Thread Break Timeout",
      field: "threadBreakTimeout",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 1, 0.0, 10.0, false, true),
      errorMessage: "Thread Break Timeout must be 0.0 to 10.0.\n",
   },
   TIME_ACCELERATION: {
      label: "Acceleration Time (secs)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "acceleration",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 5, 120),
      errorMessage: "Acceleration Time must be 5 to 120.\n",
   },
   TIME_DECELERATION: {
      label: "Deceleration Time (secs)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "deceleration",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 5, 120),
      errorMessage: "Deceleration Time must be 5 to 120.\n",
   },
   TOTAL_ENDS: {
      label: "Number of Ends",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "totalEnds",
   },
   TRAVELERS_9: {
      label: travelerLabel,
      type: ELEMENT_TYPES.TRAVELER_SIZE,
      fields: _.assign([], generateTravelerFields(9)),
      validate: (value, field, item) => validateTraveler(value, field, item),
      modify: (field, value, index, onChangeCallback, editItem, setSpecInfo, specInfo) =>
         modifyTraveler(field, value, index, onChangeCallback, editItem, setSpecInfo, specInfo),
      errorMessage: travelerErrorMessage,
   },
   TRAVERSE: {
      label: "Traverse (in)",
      field: "traverse",
      dataType: constants.DATA_TYPES.FLOAT,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 2, 0.01, 12.0, false, true),
      errorMessage: "Traverse must be 0.01 to 12.00.\n",
   },
   TUBE_COLOR: {
      label: "Supply Tube Color",
      field: "supplyTubeColor",
      dataType: constants.DATA_TYPES.TEXT,
      isRequired: () => { return true; },
      type: ELEMENT_TYPES.DEBOUNCE,
      errorMessage: "Supply Tube Color is required\n",
   },
   TUBE_OD: {
      label: "Tube OD (in)",
      field: "tubeOD",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.DEBOUNCE,
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 1, 2, 0.1, 9.99, false, true),
      errorMessage: "Tube OD must be 0.1 to 9.99.\n",
   },
   TWIST_FIRST: {
      label: "First Twist (t/m)",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "firstTwist",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999),
      errorMessage: "First Twist must be 0 to 999.\n",
   },
   TWIST_SECOND: {
      label: "Second Twist (t/m)",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "secondTwist",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999),
      errorMessage: "Second Twist must be 0 to 999.\n",
   },
   WIND_RATIO: {
      label: "Wind Ratio",
      dataType: constants.DATA_TYPES.DECIMAL,
      type: ELEMENT_TYPES.MINMAX,
      minField: "windRatioStart",
      maxField: "windRatioEnd",
      maxLength: 8,
      minLabel: "Start",
      maxLabel: "End",
      validate: (value, field) =>
         rgx.validateDecimalRange(value, field, 2, 5, 1.0, 12.99999, false, true),
      errorMessage: "Wind Ratio must be 1.00000 to 12.99999.\n",
   },
   WIND_TYPE: {
      label: "Wind Type",
      dataType: constants.DATA_TYPES.TEXT,
      type: ELEMENT_TYPES.SINGLE_SELECT,
      maxLength: 15,
      options: [
         { label: digiwind, value: digiwind },
         { label: precision, value: precision },
      ],
      isRequired: () => { return true; },
      errorMessage: "Wind Type is required.\n",
      field: "windType",
   },
   WINDING_TENSION: {
      label: "Winding Tension (g)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "windingTensionMin",
      maxField: "windingTensionMax",
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 600),
      errorMessage: "Winding Tension must be 1 to 600.\n",
   },
   WINDING_TENSION_MIN_MAX: {
      label: "Winding Tension",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "tensionMin",
      maxField: "tensionMax",
      disregardMinMax: true,
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 600),
      errorMessage: "Winding Tension must be 1 to 600.\n",
   },
   YARDS: {
      label: "Yards",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "yards",
      validate: (value, field) => rgx.validateIntegerWithCommas(value, field, 7, false, false),
      errorMessage: "Yards must be between 1 and 9,999,999\n",
   },
   YARDS_PER_PACKAGE: {
      label: "Yards Per Package (yds)",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "yardsPerPkg",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 999999),
      errorMessage: "Yards Per Package must be between 1 and 999,999\n",
   },
   YARN_LENGTH: {
      label: "Yarn Length (yds)",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "yarnLength",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 99999),
      errorMessage: "Yarn Length must be 1 to 99,999.\n",
   },
   YARN_LENGTH_CLOCK_SETTING: {
      label: "Yarn Length (Clock Setting) (m)",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "clockSet",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99999),
      errorMessage: "Yarn Length (Clock Setting) must be 1 to 99,999.\n",
   },
   YARN_SPEED: {
      label: "Yarn Speed (ypm)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "yarnSpeed",
   },
   YARN_SPEED_MIN_MAX: {
      label: "Yarn Speed (ypm)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "yarnSpeedMin",
      maxField: "yarnSpeedMax",
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 500),
      errorMessage: "Yarn Speed must be 1 to 500.\n",
   },
   YARN_TENSIONER: {
      label: "Yarn Tensioner (%)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MULTI_INPUT,
      fields: [
         { label: "High", field: "tensionHigh" },
         { label: "Low", field: "tensionLow" },
         { label: "Mid", field: "tensionMid" },
      ],
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 100),
      errorMessage: "Yarn Tensioner values must be 0 to 100.\n",
   },
   YARN_TENSIONER_LI: {
      label: "Yarn Tensioner (LI) (meters)",
      dataType: constants.DATA_TYPES.INT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "tensionLI",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99999),
      errorMessage: "Yarn Tensioner (LI) must be 0 to 99,999.\n",
   },
   ZONE_B_BAR_POSITION: {
      label: "Zone B Bar Position (°) ",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.DEBOUNCE,
      field: "zoneBBarPos",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 360),
      errorMessage: "Zone B Bar Position must be 0° to 360°.\n",
   },
   ZONE_B_TENSION: {
      label: "Zone B Tension (g)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "zoneBTensionMin",
      maxField: "zoneBTensionMax",
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 600),
      errorMessage: "Zone B Tension must be 1 to 600.\n",
   },
   ZONE_C_TENSION: {
      label: "Zone C Tension (g)",
      dataType: constants.DATA_TYPES.SMALLINT,
      type: ELEMENT_TYPES.MINMAX,
      minField: "zoneCTensionMin",
      maxField: "zoneCTensionMax",
      minLabel: "Min",
      maxLabel: "Max",
      validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 600),
      errorMessage: "Zone C Tension must be 1 to 600.\n",
   },
};

function formatValues(field, value, machineType) {
   if (value === null || value === "") {
      return null;
   }
   switch (field) {
      case "applicatorRollRPM":
      case "backPressureBegin":
      case "backPressureEnd":
      case "clockSet":
      case "clockSetting":
      case "counterSet":
      case "decelSpeed":
      case "deckLower":
      case "deckUpper":
      case "gearA":
      case "gearB":
      case "gearC":
      case "gearD":
      case "gearE":
      case "gearF":
      case "initialSpeed":
      case "impulseSet":
      case "length":
      case "pounds":
      case "productionSpeed":
      case "rollerCoefficient":
      case "spacing":
      case "speed":
      case "speedEnd":
      case "speedLower":
      case "speedMiddle":
      case "speedStart":
      case "speedUpper":
      case "spindleSpeed":
      case "tensionLI":
      case "underwindS":
      case "yards":
      case "yardsPerPkg":
      case "yarnLength":
         return helpers.commify(value);
      case "backPressMin":
      case "backPressMax":
      case "begBackPressureMax":
      case "begBackPressureMin":
      case "endBackPressureMax":
      case "endBackPressureMin":
      case "layWheelSet":
      case "tensionPostSet":
      case "traverse":
      case "tubeOD":
         return parseFloat(value).toFixed(2);
      case "boxStretch":
      case "celsiusMin1":
      case "celsiusMin2":
      case "celsiusMin3":
      case "cycleVariable":
      case "delayLower":
      case "delayUpper":
      case "fAnti":
      case "fMinLower":
      case "graf":
      case "hAnti":
      case "leftTaper":
      case "maxStroke":
      case "maxStrokeCount":
      case "maxStrokeCycle":
      case "minStroke":
      case "rightTaper":
      case "ringSpeedDown":
      case "ringSpeedUp":
      case "ringStartPos":
      case "rollerDiameter":
      case "screwPitch":
      case "spindleWraps":
      case "startPos":
      case "strokeIncrement":
      case "tensionSet":
      case "threadGuide":
      case "timeConveyor":
      case "traverseMax":
      case "traverseMin":
      case "traverseSpeed":
      case "trimAmount":
      case "underwindL":
         return parseFloat(value).toFixed(1);
      case "scaleFactor":
      case "windRatioEnd":
      case "windRatioStart":
         return parseFloat(value).toFixed(5);
      case "windRatiogirthEnd":
         return parseFloat(value).toFixed(4);
      case "underwindD":
         switch (machineType) {
            case constants.MACHINE_SETUP.HACOBA:
               return helpers.commify(parseFloat(value).toFixed(1));
            case constants.MACHINE_SETUP.HACOBA_SMALL:
               return parseFloat(value).toFixed(1);
            default:
               return value;
         }
      case "underwindT":
         switch (machineType) {
            case constants.MACHINE_SETUP.HACOBA_SMALL:
               return helpers.commify(parseFloat(value).toFixed(1));
            default:
               return value;
         }
      case "underwindV":
         return helpers.commify(parseFloat(value).toFixed(1));
      default:
         return value;
   }
}

function generateTravelerFields(numberOfTravelers) {
   let fields = [];
   for (let i = 0; i < numberOfTravelers; i++) {
      fields.push({
         label: "Driver",
         field: `traveler${i + 1}`,
         dataType: constants.DATA_TYPES.BOOL,
      });
      fields.push({
         label: "Driver",
         field: `travelerQty${i + 1}`,
         dataType: constants.DATA_TYPES.SMALLINT,
      });
   }
   return _.sortBy(fields, (f) => {
      return f.field;
   });
}

const getMachineTypes = (calcType, machineType, editItem) => {
   // const isAlternate = calcType === constants.CALCULATION_TYPES.ALTERNATE.value;
   // const isSoleSetup = calcType === constants.CALCULATION_TYPES.SOLE_SETUP.value;
   const isCumuluative = calcType === constants.CALCULATION_TYPES.CUMULATIVE.value;

   let MachineTypes = {
      "861FC": {
         value: 1,
         label: "Leesona 861 FC Winder",
         elements: [
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.BEG_BACK_PRESSURE),
            _.assign({}, commonElem.BEG_TENSION),
            _.assign({}, commonElem.YARN_SPEED_MIN_MAX),
            _.assign({}, commonElem.GRAF),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear C must be 10 to 30.",
               },
               commonElem.GEAR_C
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear D must be 10 to 30.",
               },
               commonElem.GEAR_D
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear E must be 10 to 30.",
               },
               commonElem.GEAR_E
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear F must be 10 to 30.",
               },
               commonElem.GEAR_F
            ),
            _.assign({}, commonElem.YARDS),
            _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      "861Tack": {
         value: 5,
         label: "Leesona 861 Tackified Winder",
         elements: [
            _.assign({}, commonElem.CLOCK_SETTING),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.BEG_BACK_PRESSURE),
            _.assign({}, commonElem.BEG_TENSION),
            {
               label: "Differential Cam Link",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "diffCamLink",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9),
               errorMessage: "Differential Cam Link must be 1 to 9.",
            },
            {
               label: "Tension Adjustment Bracket",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "tensionAdj",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 6),
               errorMessage: "Differential Cam Link must be 1 to 6.",
            },
            _.assign({}, commonElem.CREEL_TENSION),
            _.assign({}, commonElem.YARN_SPEED),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear C must be 10 to 30.",
               },
               commonElem.GEAR_C
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear D must be 10 to 30.",
               },
               commonElem.GEAR_D
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear E must be 10 to 30.",
               },
               commonElem.GEAR_E
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear F must be 10 to 30.",
               },
               commonElem.GEAR_F
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Applicator Roll RPM must be 10 to 30.",
               },
               commonElem.APPLICATOR_ROLL_RPM
            ),
            _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      "861Wax": {
         value: 6,
         label: "Leesona 861 Wax Winder",
         elements: [
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.BEG_BACK_PRESSURE),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear C must be 10 to 30.",
               },
               commonElem.GEAR_C
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear D must be 10 to 30.",
               },
               commonElem.GEAR_D
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear E must be 10 to 30.",
               },
               commonElem.GEAR_E
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 10, 30),
                  errorMessage: "Gear F must be 10 to 30.",
               },
               commonElem.GEAR_F
            ),
            {
               label: "Counter FC71",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "counterFC71",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 99),
               errorMessage: "Counter FC71 must be 1 to 99.",
            },
            {
               label: "Counter FC72",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "counterFC72",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 99),
               errorMessage: "Counter FC72 must be 1 to 99.",
            },
            {
               label: "Wax Temp (°F)",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "waxTemp",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 100, 300),
               errorMessage: "Wax Temp must be 100 to 300.",
            },
            {
               label: "Wiper Weights",
               dataType: constants.DATA_TYPES.TEXT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "wiperWeights",
               maxLength: 50,
            },
            _.assign({}, commonElem.YARDS),
            _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Doubler: {
         value: 7,
         label: "Gilbos Doubler",
         elements: _.reject(
            [
               _.assign({}, commonElem.CLOCK_SETTING),
               _.assign({}, commonElem.RUNTIME),
               _.assign(
                  {
                     validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 99),
                     errorMessage: "Program # must be 1 to 99.",
                  },
                  commonElem.PROGRAM_NUMBER
               ),
               _.assign(
                  {
                     validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 15),
                     errorMessage: "Number of Ends must be 1 to 15.",
                  },
                  commonElem.TOTAL_ENDS
               ),
               _.assign({}, commonElem.CREEL_TENSION),
               _.assign({}, commonElem.WINDING_TENSION),
               _.assign(
                  {
                     validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 999),
                     errorMessage: "Spindle Speed must be 1 to 999.",
                  },
                  commonElem.SPINDLE_SPEED
               ),
               _.assign({}, commonElem.STD_EFFICIENCY),
               _.assign({}, commonElem.STD_SPINDLE_COUNT),
               isCumuluative
                  ? {
                     label: "Yarn Joining Type",
                     dataType: constants.DATA_TYPES.SMALLINT,
                     type: ELEMENT_TYPES.SINGLE_SELECT,
                     field: "wiperWeights",
                     options: _.toArray(constants.YARN_JOINING_TYPES),
                     isRequired: () => { return true; },
                  }
                  : null,
               isCumuluative
                  ? {
                     label: "Max Splices Per Package",
                     dataType: constants.DATA_TYPES.SMALLINT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     field: "maxSplices",
                     isRequired: () => { return true; },
                     validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999),
                     errorMessage: "Max Splices Per Package must be 0 to 999.",
                  }
                  : null,
               isCumuluative
                  ? {
                     label: "Put Up Color / Size",
                     dataType: constants.DATA_TYPES.TEXT,
                     maxLength: 50,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     field: "putUpSize",
                     isRequired: () => { return true; },
                  }
                  : null,
               _.assign({}, commonElem.LABOR_PCT),
            ],
            (x) => {
               return _.isNull(x);
            }
         ),
      },
      DP5: {
         value: 21,
         label: "SSM DP5 Winder",
         elements: [
            _.assign({}, commonElem.PROGRAM_NAME),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.TUBE_OD),
            _.assign({}, commonElem.YARDS_PER_PACKAGE),
            _.assign(
               {
                  fields: [
                     { label: "Start", field: "speedStart" },
                     { label: "Middle", field: "speedMiddle" },
                     { label: "End", field: "speedEnd" },
                  ],
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 50, 9999),
                  errorMessage: "Speed values must be 50 to 9,999.",
               },
               commonElem.MULTI_SPEED
            ),
            _.assign({}, commonElem.TRAVERSE),
            _.assign({}, commonElem.LEFT_TAPER),
            _.assign({}, commonElem.LEFT_COMPENSATION),
            _.assign({}, commonElem.RIGHT_TAPER),
            _.assign({}, commonElem.RIGHT_COMPENSATION),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9999),
                  errorMessage: "Back Pressure must be 1 to 9,999.",
               },
               commonElem.BACK_PRESSURE_BEGIN_END
            ),
            _.assign({}, commonElem.WIND_TYPE),
            editItem?.windType === digiwind
               ? _.assign({}, commonElem.DEGREES)
               : _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.WINDING_TENSION),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      DS10E: {
         value: 32,
         label: "DS10-E Winder",
         elements: [
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9999),
                  errorMessage: "Program # must be 1 to 9,999.",
               },
               commonElem.PROGRAM_NUMBER
            ),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.YARN_LENGTH),
            _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.WINDING_TENSION_MIN_MAX),
            _.assign({}, commonElem.CAPSTAN_WRAPS),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      DS21: {
         value: 2,
         label: "Dietze Schell DS-21/31 Winder",
         elements: [
            _.assign({}, commonElem.CLOCK_SETTING),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 5),
                  errorMessage: "Speed Setting must be 1 to 5.",
               },
               commonElem.SPEED_SETTING
            ),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.BEG_BACK_PRESSURE),
            _.assign({}, commonElem.END_BACK_PRESSURE),
            _.assign({}, commonElem.BEG_TENSION),
            {
               label: "Number Fields",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "numFields",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 4, 60),
               errorMessage: "Number Fields must be 4 to 60.",
            },
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 12, 50),
                  errorMessage: "Gear A must be 12 to 50.",
               },
               commonElem.GEAR_A
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 12, 50),
                  errorMessage: "Gear B must be 12 to 50.",
               },
               commonElem.GEAR_B
            ),
            {
               label: "Tension Release Speed Arm",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "tensionSpeedArm",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 5),
               errorMessage: "Gear B must be 1 to 5.",
            },
            {
               label: "Spring Pos Top",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "springPosTop",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 5),
               errorMessage: "Spring Position Top must be 1 to 5.",
            },
            {
               label: "Spring Pos Bottom",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "springPosBottom",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 5),
               errorMessage: "Spring Position Bottom must be 1 to 5.",
            },
            {
               label: "Spring Plate",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "springPlate",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 3),
               errorMessage: "Spring Plate must be 1 to 3.",
            },
            _.assign(
               {
                  validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 40.0, false, true),
                  errorMessage: "Applicator Roll RPM must be 0.00 to 40.0.",
               },
               commonElem.APPLICATOR_ROLL_RPM
            ),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      GriffinOven: {
         value: 18,
         label: "Griffin Oven",
         elements: [
            _.assign({}, commonElem.BOX_PRESSURE),
            _.assign(
               {
                  validate: (value, field) => rgx.validateDecimalRange(value, field, 1, 1, -3.0, 3.0, true, true),
                  errorMessage: "Over Feed/Size Box Stretch must be -3.0 to 3.0.",
               },
               commonElem.BOX_STRETCH
            ),
            _.assign({}, commonElem.BOND_TEMP),
            _.assign({}, commonElem.BOX_SETTING),
            {
               label: "Wiper Zone A Weight",
               field: "wiperAWeight",
               dataType: constants.DATA_TYPES.TEXT,
               type: ELEMENT_TYPES.DEBOUNCE,
               maxLength: 10,
               isRequired: () => { return false; },
            },
            {
               label: "Wiper Zone B Weight",
               field: "wiperBWeight",
               dataType: constants.DATA_TYPES.TEXT,
               type: ELEMENT_TYPES.DEBOUNCE,
               maxLength: 10,
               isRequired: () => { return false; },
            },
            {
               label: "Wiper Zone C Weight",
               field: "wiperCWeight",
               dataType: constants.DATA_TYPES.TEXT,
               type: ELEMENT_TYPES.DEBOUNCE,
               maxLength: 10,
               isRequired: () => { return false; },
            },
            _.assign({}, commonElem.CLOCK_SETTING),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.OVEN_1_TEMP),
            {
               label: "#2 Oven Temp (°F) ",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "oven2Temp",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 60, 600),
               errorMessage: "Oven Temps must be 60 to 600.",
            },
            {
               label: "#2 Oven Exhaust (%) ",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "oven2Exhaust",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 100),
               errorMessage: "Oven Temps must be 0 to 100.",
            },
            _.assign({}, commonElem.NUMBER_PASSES),
            _.assign({}, commonElem.PREFEED_PRESSURE),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 250),
                  errorMessage: "Delivery Speed must be 0 to 250.",
               },
               commonElem.DELIVERY_SPEED
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
                  errorMessage: "Pre-Feeder Oscillator Setting must be 0 to 99.",
               },
               commonElem.PREFEED_SETTING
            ),
            _.assign({}, commonElem.CREEL_TYPE),
            // these are in the model & code, but not on the qa site, so removing for now
            // _.assign({}, commonElem.CREEL_TENSION),
            // _.assign({}, commonElem.ZONE_B_BAR_POSITION),
            // _.assign({}, commonElem.ZONE_B_TENSION),
            // _.assign({}, commonElem.ZONE_C_TENSION),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Hacoba: {
         value: 20,
         label: "Hacoba NSA-HS Winder",
         elements: [
            _.assign({}, commonElem.IMPULSE_SET),
            _.assign({}, commonElem.RUNTIME),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 500, 1955),
                  errorMessage: "Delivery Speed must be 500 to 1,955.",
               },
               commonElem.SPEED
            ),
            _.assign({}, commonElem.LUBE_ROLLER),
            _.assign({}, commonElem.TENSION),
            {
               label: "Time Deposit",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "timeDeposit",
               dataType: constants.DATA_TYPES.SMALLINT,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 10),
               errorMessage: "Time Deposit must be 0 to 10.",
            },
            _.assign({}, commonElem.THREAD_BREAK_TIMEOUT),
            {
               label: "Underwind S",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "underwindS",
               dataType: constants.DATA_TYPES.SMALLINT,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99999),
               errorMessage: "Underwind S must be 0 to 99,999.",
            },
            {
               label: "Underwind D",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "underwindD",
               dataType: constants.DATA_TYPES.FLOAT,
               validate: (value, field) => rgx.validateDecimalRange(value, field, 4, 1, 0.0, 9999.9, false, true),
               errorMessage: "Underwind D must be 0.0 to 9,999.9.",
            },
            {
               label: "Underwind L",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "underwindL",
               dataType: constants.DATA_TYPES.FLOAT,
               validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 10.0, false, true),
               errorMessage: "Underwind L must be 0.0 to 10.0.",
            },
            {
               label: "Thread Guide (sec)",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "threadGuide",
               dataType: constants.DATA_TYPES.FLOAT,
               validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 10.0, false, true),
               errorMessage: "Thread Guide must be 0.0 to 10.0.",
            },
            {
               label: "Time Conveyor (sec)",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "timeConveyor",
               dataType: constants.DATA_TYPES.FLOAT,
               validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 10.0, false, true),
               errorMessage: "Time Conveyor must be 0.0 to 10.0.",
            },
            _.assign({}, commonElem.LAY_WHEEL_SET),
            _.assign({}, commonElem.TENSION_POST_SET),
            _.assign({}, commonElem.BACK_PRESSURE_SPRING),
            _.assign({}, commonElem.FEEDER_TENSION_SET),
            _.assign({}, commonElem.FEEDER_WRAPS),
            _.assign({}, commonElem.YARDS),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      // HacobaSmall: {
      //   value: 23,
      //   label: "Hacoba NSA-U (Small) Winder",
      //   elements: [
      //     {
      //       label: "Impulse setting ",
      //       type: ELEMENT_TYPES.DEBOUNCE,
      //       field: "impulseSet",
      //       dataType: constants.DATA_TYPES.SMALLINT,
      //       isRequired: () => { return true; },
      //       validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 4700),
      //       errorMessage: "Impulse Setting must be 1 to 4,700.",
      //     },
      //     _.assign({}, commonElem.RUNTIME),
      //     {
      //       label: "Lube Roller",
      //       dataType: constants.DATA_TYPES.DOUBLE,
      //       type: ELEMENT_TYPES.MINMAX,
      //       minField: "lubeRollerStart",
      //       maxField: "lubeRollerMax",
      //       disregardMinMax: true,
      //       minLabel: "Start",
      //       maxLabel: "Max",
      //       validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 10.0, false, true),
      //       errorMessage: "Lube Roller must be 0.00 to 10.0.",
      //     },
      //     _.assign({}, commonElem.TENSION),
      //     _.assign({}, commonElem.THREAD_BREAK_TIMEOUT),
      //     _.assign({}, commonElem.STD_EFFICIENCY),
      //     _.assign({}, commonElem.STD_SPINDLE_COUNT),
      //     _.assign({}, commonElem.LABOR_PCT),
      //   ],
      // },
      HeliKV2019B: {
         value: 35,
         label: "Heli KV2019B Twister",
         elements: [
            _.assign({}, commonElem.CLOCK_SET_SIX),
            _.assign({}, commonElem.RUNTIME),
            {
               label: null,
               type: ELEMENT_TYPES.MULTI_GRID,
               colSize: 3,
               fields: [
                  _.assign({}, commonElem.PARAMETER_SETTING_1),
                  _.assign({}, commonElem.MACHINE_SETTING_1),
                  _.assign(
                     {
                        validate: (value, field) => rgx.validateIntegerRange(value, field, 1896, 10500),
                        errorMessage: "Upper Spindle Speed must be 1,896 to 10,500.",
                     },
                     commonElem.SPEED_UPPER
                  ),
                  _.assign({}, commonElem.SCREW_PITCH),
                  _.assign({}, commonElem.SPEED_LOWER),
                  _.assign({}, commonElem.ENCODER_IMPULSE),
                  _.assign({}, commonElem.RING_SPEED_UP),
                  _.assign({}, commonElem.DECK_UPPER),
                  _.assign({}, commonElem.RING_SPEED_DOWN),
                  _.assign(
                     {
                        validate: (value, field) => rgx.validateIntegerRange(value, field, 2000, 15000),
                        errorMessage: "Lower Deck must be 2,000 to 15,000.",
                     },
                     commonElem.DECK_LOWER
                  ),
                  _.assign({}, commonElem.RING_START_POS),
                  _.assign({}, commonElem.FAULT_WARNING),
                  _.assign({}, commonElem.STROKE_MAX),
                  _.assign({}, commonElem.SPEED_MONITORING),
                  _.assign({}, commonElem.STROKE_MIN),
                  _.assign({}, commonElem.SPINDLE_DIRECTION),
                  { showBreak: true },
                  { showBreak: true },
                  _.assign({}, commonElem.PARAMETER_SETTING_2),
                  _.assign({}, commonElem.MACHINE_SETTING_2),
                  _.assign({}, commonElem.PACKAGE_TYPE),
                  _.assign({}, commonElem.TIME_ACCELERATION),
                  _.assign({}, commonElem.STROKE_INCREMENT),
                  _.assign({}, commonElem.TIME_DECELERATION),
                  _.assign({}, commonElem.F_ANTI),
                  _.assign({}, commonElem.DELAY_UPPER),
                  _.assign({}, commonElem.H_ANTI),
                  _.assign({}, commonElem.DELAY_LOWER),
                  _.assign({}, commonElem.STROKE_MAX_CYCLE),
                  { showBreak: true },
                  _.assign({}, commonElem.STROKE_MAX_COUNT),
                  { showBreak: true },
                  { showBreak: true },
                  { showBreak: true },
                  _.assign({}, commonElem.PARAMETER_SETTING_3),
                  _.assign({}, commonElem.MACHINE_SETTING_3),
                  _.assign({}, commonElem.TWIST_FIRST),
                  _.assign({}, commonElem.ROLLER_DIAMETER),
                  _.assign({}, commonElem.TWIST_SECOND),
                  _.assign({}, commonElem.ROLLER_COEFFICIENT),
                  _.assign({}, commonElem.COUNTER_SWITCH),
                  _.assign({}, commonElem.ROLLER_IMPULSE),
                  _.assign({}, commonElem.COUNTER_SET),
                  _.assign({}, commonElem.PULSE_RATE),
                  { showBreak: true },
                  _.assign({}, commonElem.RUN_RESET),
                  { showBreak: true },
                  _.assign({}, commonElem.FMIN_LOWER),
               ],
            },
            _.assign({}, commonElem.TENSION_BALL_SPINDLE),
            _.assign({}, commonElem.TENSION_BALL_TOP),
            _.assign({}, commonElem.TRAVELERS_9),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      HeliPirn: {
         value: 29,
         label: "Heli KV2002 Pirn Winder",
         elements: [
            _.assign({}, commonElem.RUNTIME),
            {
               label: "Length (m)",
               dataType: constants.DATA_TYPES.INT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "length",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999999),
               errorMessage: "Length must be 0 to 999,999.",
            },
            {
               label: "Girth",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "girth",
               validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 4, 0.0503, 0.0503, false, true),
               errorMessage: "Girth must be 0.0503.",
            },
            {
               label: "F1",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "f1",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
               errorMessage: "F1 must be 0 to 99.",
            },
            {
               label: "Type",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "type",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 1),
               errorMessage: "Type must be 1.",
            },
            {
               label: "Spindle Setup/Speed (rpm)",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "spindleSpeed",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 5925),
               errorMessage: "Spindle Setup/Speed must be 0 to 5,925.",
            },
            {
               label: "Ring Plate/Traverse Speed (mm/s)",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "traverseSpeed",
               validate: (value, field) => rgx.validateDecimalRange(value, field, 3, 1, 0.0, 553.5, false, true),
               errorMessage: "Ring Plate/Traverse Speed must be 0.0 to 553.5.",
            },
            {
               label: "Itinerary/Traverse (mm)",
               dataType: constants.DATA_TYPES.DECIMAL,
               type: ELEMENT_TYPES.MINMAX,
               minField: "traverseMin",
               maxField: "traverseMax",
               maxLength: 5,
               minLabel: "Min",
               maxLabel: "Max",
               validate: (value, field) => rgx.validateDecimalRange(value, field, 3, 1, 0.0, 553.5, false, true),
               errorMessage: "Itinerary/Traverse must be 0.0 to 553.5.",
            },
            {
               label: "Starting Pos (mm)",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "startPos",
               validate: (value, field) => rgx.validateDecimalRange(value, field, 3, 1, 0.0, 553.5, false, true),
               errorMessage: "Starting Pos must be 0.0 to 553.5.",
            },
            {
               label: "Cycle Variable (mm)",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "cycleVariable",
               validate: (value, field) => rgx.validateDecimalRange(value, field, 3, 1, 0.0, 53.5, false, true),
               errorMessage: "Cycle Variable must be 0.0 to 53.5.",
            },
            {
               label: "Bottom Trim",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
               sideLabel: "/ 20",
               field: "bottomTrim",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 35),
               errorMessage: "Bottom Trim must be 0 to 35.",
            },
            {
               label: "Trim Amount (mm)",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "trimAmount",
               validate: (value, field) => rgx.validateDecimalRange(value, field, 3, 1, 0.0, 53.5, false, true),
               errorMessage: "Trim Amount must be 0.0 to 53.5.",
            },
            {
               label: "Spindle Tension (g)",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.MINMAX,
               minField: "tensionMin",
               maxField: "tensionMax",
               minLabel: "Min",
               maxLabel: "Max",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 600),
               errorMessage: "Tension must be 1 to 600.",
            },
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      HeliTwister: {
         value: 30,
         label: "Heli KV2008B Twister",
         elements: [
            _.assign({}, commonElem.CLOCK_SET_SIX),
            _.assign({}, commonElem.RUNTIME),
            {
               label: null,
               type: ELEMENT_TYPES.MULTI_GRID,
               colSize: 3,
               fields: [
                  _.assign({}, commonElem.PARAMETER_SETTING_1),
                  _.assign({}, commonElem.MACHINE_SETTING_1),
                  _.assign(
                     {
                        validate: (value, field) => rgx.validateIntegerRange(value, field, 1000, 10500),
                        errorMessage: "Upper Spindle Speed must be 1,000 to 10,500.",
                     },
                     commonElem.SPEED_UPPER
                  ),
                  _.assign({}, commonElem.SCREW_PITCH),
                  _.assign({}, commonElem.SPEED_LOWER),
                  _.assign({}, commonElem.ENCODER_IMPULSE),
                  _.assign({}, commonElem.RING_SPEED_UP),
                  _.assign({}, commonElem.DECK_UPPER),
                  _.assign({}, commonElem.RING_SPEED_DOWN),
                  _.assign(
                     {
                        validate: (value, field) => rgx.validateIntegerRange(value, field, 5000, 15000),
                        errorMessage: "Lower Deck must be 5,000 to 15,000.",
                     },
                     commonElem.DECK_LOWER
                  ),
                  _.assign({}, commonElem.RING_START_POS),
                  _.assign({}, commonElem.FAULT_WARNING),
                  _.assign({}, commonElem.STROKE_MAX),
                  _.assign({}, commonElem.SPEED_MONITORING),
                  _.assign({}, commonElem.STROKE_MIN),
                  _.assign({}, commonElem.SPINDLE_DIRECTION),
                  { showBreak: true },
                  { showBreak: true },
                  _.assign({}, commonElem.PARAMETER_SETTING_2),
                  _.assign({}, commonElem.MACHINE_SETTING_2),
                  _.assign({}, commonElem.PACKAGE_TYPE),
                  _.assign({}, commonElem.TIME_ACCELERATION),
                  _.assign({}, commonElem.STROKE_INCREMENT),
                  _.assign({}, commonElem.TIME_DECELERATION),
                  _.assign({}, commonElem.F_ANTI),
                  _.assign({}, commonElem.DELAY_UPPER),
                  _.assign({}, commonElem.H_ANTI),
                  _.assign({}, commonElem.DELAY_LOWER),
                  _.assign({}, commonElem.STROKE_MAX_CYCLE),
                  { showBreak: true },
                  _.assign({}, commonElem.STROKE_MAX_COUNT),
                  { showBreak: true },
                  { showBreak: true },
                  { showBreak: true },
                  _.assign({}, commonElem.PARAMETER_SETTING_3),
                  _.assign({}, commonElem.MACHINE_SETTING_3),
                  _.assign({}, commonElem.TWIST_FIRST),
                  _.assign({}, commonElem.ROLLER_DIAMETER),
                  _.assign({}, commonElem.TWIST_SECOND),
                  _.assign({}, commonElem.ROLLER_COEFFICIENT),
                  _.assign({}, commonElem.COUNTER_SWITCH),
                  _.assign({}, commonElem.ROLLER_IMPULSE),
                  _.assign({}, commonElem.COUNTER_SET),
                  _.assign({}, commonElem.PULSE_RATE),
                  { showBreak: true },
                  _.assign({}, commonElem.RUN_RESET),
                  { showBreak: true },
                  _.assign({}, commonElem.FMIN_LOWER),
               ],
            },
            _.assign({}, commonElem.TENSION_BALL_SPINDLE),
            _.assign({}, commonElem.TENSION_BALL_TOP),
            _.assign({}, commonElem.TRAVELERS_9),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      M50: {
         value: 3,
         label: "Leesona M50 Winder",
         elements: [
            _.assign({}, commonElem.CLOCK_SET),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.WINDING_TENSION),
            _.assign({}, commonElem.BACK_PRESS),
            {
               label: "Counter Scale Factor",
               field: "scaleFactor",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
            },
            {
               label: "Drive Type",
               field: "gearDriven",
               dataType: constants.DATA_TYPES.BOOL,
               type: ELEMENT_TYPES.RADIO,
               options: [
                  { label: "Gear Driven", value: true },
                  { label: "Belt Driven", value: false },
               ],
               validate: validateBoolean,
            },
            {
               label: "Spindle Gear",
               field: "gearSpindle",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 20, 102),
               errorMessage: "Spindle Gear must be 20 to 102.",
            },
            {
               label: "Intermediate Gear",
               field: "gearIntermediate",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 20, 102),
               errorMessage: "Intermediate Gear must be 20 to 102.",
            },
            {
               label: "Gainer Gear",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.MINMAX,
               minField: "gearInnerGainer",
               maxField: "gearOuterGainer",
               minLabel: "Inner",
               maxLabel: "Outer",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 20, 102),
               errorMessage: "Gainer Gear must be 20 to 102.",
            },
            {
               label: "Cam Gear",
               field: "gearCam",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 20, 102),
               errorMessage: "Cam Gear must be 20 to 102.",
            },
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      M50Wax: {
         value: 16,
         label: "Leesona M50 Wax Winder",
         elements: [
            _.assign({}, commonElem.CLOCK_SET),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.WINDING_TENSION),
            _.assign({}, commonElem.BACK_PRESS),
            {
               label: "Counter Scale Factor",
               field: "scaleFactor",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
            },
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      MahanDryer: {
         value: 39,
         label: "Mahan Dryer",
         elements: [
            {
               label: "High Limit",
               dataType: constants.DATA_TYPES.INT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "highLimit",
               isRequired: () => { return true; },
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 999),
               errorMessage: "High Limit must be 1 to 999.",
            },
            {
               label: "Temperature Controller",
               dataType: constants.DATA_TYPES.INT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "tempControl",
               isRequired: () => { return true; },
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 999),
               errorMessage: "Temperature Controller must be 1 to 999.",
            },
            {
               label: "Timer Set (HH:MM:SS) ",
               field: "runTime",
               dataType: constants.DATA_TYPES.TEXT,
               maxLength: 10,
               isRequired: () => { return true; },
               type: ELEMENT_TYPES.DEBOUNCE,
               validate: (value, field) => rgx.validateHMS(value, field),
               errorMessage: "Timer Set must be in HH:MM:SS format",
            },
            {
               label: "Circulation Fan",
               field: "fan",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.RADIO,
               options: stopStartOptions,
               isRequired: () => { return true; },
               errorMessage: "Circulation Fan is required",
            },
            {
               label: "Exhaust & Combustion",
               field: "exhaust",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.RADIO,
               options: stopStartOptions,
               isRequired: () => { return true; },
               errorMessage: "Exhaust & Combustion is required",
            },
            {
               label: "Burner",
               field: "burner",
               dataType: constants.DATA_TYPES.BOOL,
               type: ELEMENT_TYPES.RADIO,
               options: onOffOptions,
               validate: validateBoolean,
               isRequired: () => { return true; },
               errorMessage: "Burner is required",
            },
            {
               label: "Timer",
               field: "timer",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.RADIO,
               options: stopStartOptions,
               isRequired: () => { return true; },
               errorMessage: "Timer is required",
            },
            {
               label: "Circulation Speed",
               field: "circulationSpeed",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.RADIO,
               options: [
                  { label: "Low", value: 0 },
                  { label: "Medium", value: 1 },
                  { label: "High", value: 2 },
               ],
               isRequired: () => { return true; },
               errorMessage: "Circulation Speed is required",
            },
            {
               label: "Yield Pounds",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "pounds",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 2000, 2000),
               errorMessage: "Yield Pounds must be 2,000.",
            },
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      MahanOven: {
         value: 27,
         label: "Mahan Oven",
         elements: [
            _.assign({}, commonElem.BOX_PRESSURE),
            _.assign({
                  validate: (value, field) => rgx.validateDecimalRange(value, field, 1, 1, -7.0, 7.0, true, true),
                  errorMessage: "Over Feed/Size Box Stretch must be -7.0 to 7.0.",
               },
               commonElem.BOX_STRETCH
            ),
            _.assign({}, commonElem.BOND_TEMP),
            _.assign({}, commonElem.BOX_SETTING),
            {
               label: "Wiper Weight",
               field: "wiperWeight",
               dataType: constants.DATA_TYPES.TEXT,
               type: ELEMENT_TYPES.DEBOUNCE,
               maxLength: 10,
               isRequired: () => { return false; },
            },
            _.assign({}, commonElem.CLOCK_SETTING_SIX),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.RECIPE),
            _.assign({}, commonElem.OVEN_1_TEMP),
            {
               label: "#1 Oven Control Panel",
               dataType: constants.DATA_TYPES.BOOL,
               type: ELEMENT_TYPES.MULTI_BOOL,
               fields: [
                  { label: "Top", field: "oven1PanelTop" },
                  { label: "Bottom", field: "oven1PanelBtm" },
               ],
               trueLabel: "On",
               falseLabel: "Off",
               isRequired: () => { return true; },
            },
            {
               label: "#2 Oven Temp (°F) ",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.MINMAX,
               minField: "oven2TempTop",
               maxField: "oven2TempBtm",
               disregardMinMax: true,
               minLabel: "Top",
               maxLabel: "Bottom",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 60, 600),
               errorMessage: "Oven Temps must be 60 to 600.",
            },
            {
               label: "#2 Oven Control Panel",
               dataType: constants.DATA_TYPES.BOOL,
               type: ELEMENT_TYPES.MULTI_BOOL,
               fields: [
                  { label: "Top", field: "oven2PanelTop" },
                  { label: "Bottom", field: "oven2PanelBtm" },
               ],
               trueLabel: "On",
               falseLabel: "Off",
               isRequired: () => { return true; },
            },
            _.assign({}, commonElem.NUMBER_PASSES),
            _.assign({}, commonElem.PREFEED_PRESSURE),
            _.assign({
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 300),
                  errorMessage: "Delivery Speed must be 0 to 300.",
               },
               commonElem.DELIVERY_SPEED
            ),
            _.assign({
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 10),
                  errorMessage: "Pre-Feeder Oscillator Setting must be 0 to 10.",
               },
               commonElem.PREFEED_SETTING
            ),
            _.assign({}, commonElem.DANCER_ARM_TENSION),
            _.assign({}, commonElem.CREEL_TYPE),
            // these fields are on the table but not in the old site???
            // _.assign({}, commonElem.CREEL_TENSION),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Other: { value: 4, label: "Other", elements: [] },
      Ratera: {
         value: 19,
         label: "Ratera 4PVU/330 Winder",
         elements: [
            _.assign({}, commonElem.CLOCK_SET_SIX),
            _.assign({}, commonElem.RUNTIME),
            {
               label: "Linear Drive Setting",
               dataType: constants.DATA_TYPES.FLOAT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "linearSet",
               validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 10.0, false, true),
               errorMessage: "Linear Drive Setting must be 0.00 to 10.0.",
            },
            _.assign({}, commonElem.CREEL_TENSION),
            _.assign({}, commonElem.WINDING_TENSION),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      SacoRTR: {
         value: 14,
         label: "Saco Lowell Meadows Ring Twister",
         elements: [
            _.assign({}, commonElem.CLOCK_SET),
            _.assign({}, commonElem.RUNTIME),
            _.assign(commonElem.SPINDLE_SPEED, {
               label: "Spindle Speed (rpm)",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 4000),
               errorMessage: "Spindle Speed must be 1 to 4,000.",
            }),
            {
               label: "Twist Change Gear",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "changeGear",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 24, 49),
               errorMessage: "Twist Change Gear must be 24 to 49.",
            },
            {
               label: "Twist Constant Gear",
               type: ELEMENT_TYPES.MULTI_INPUT,
               fields: [
                  {
                     label: "Driver",
                     field: "driverConstant",
                     dataType: constants.DATA_TYPES.SMALLINT,
                  },
                  {
                     label: "Driven",
                     field: "drivenConstant",
                     dataType: constants.DATA_TYPES.SMALLINT,
                  },
               ],
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
               errorMessage: "Twist Constants must be 0 to 99.",
            },
            {
               label: travelerLabel,
               type: ELEMENT_TYPES.TRAVELER_SIZE,
               fields: _.assign([], generateTravelerFields(4)),
               validate: (value, field, item) => validateTraveler(value, field, item),
               modify: (field, value, index, onChangeCallback, editItem, setSpecInfo, specInfo) =>
                  modifyTraveler(field, value, index, onChangeCallback, editItem, setSpecInfo, specInfo),
               errorMessage: travelerErrorMessage,
            },
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Sahm: {
         value: 13,
         label: "Sahm 230E",
         elements: [
            _.assign({}, commonElem.CLOCK_SET),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.CREEL_TENSION),
            _.assign({}, commonElem.BACK_PRESS),
            _.assign({}, commonElem.WINDING_TENSION),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 18, 102),
                  errorMessage: "Gear C must be 18 to 102.",
               },
               commonElem.GEAR_C
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 18, 102),
                  errorMessage: "Gear D must be 18 to 102.",
               },
               commonElem.GEAR_D
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 18, 102),
                  errorMessage: "Gear E must be 18 to 102.",
               },
               commonElem.GEAR_E
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 18, 102),
                  errorMessage: "Gear F must be 18 to 102.",
               },
               commonElem.GEAR_F
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 40.0, false, true),
                  errorMessage: "Applicator Roll RPM must be 0.00 to 40.0.",
               },
               commonElem.APPLICATOR_ROLL_RPM
            ),
            {
               label: "Pre-Feed Roll Pressure (psi)",
               field: "preFeedPressure",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 99),
               errorMessage: "Pre-Feed Roll Pressure must be 0 to 99.",
            },
            {
               label: "Delivery Speed (ypm)",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "deliverySpeed",
               dataType: constants.DATA_TYPES.INT,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 500),
               errorMessage: "Delivery Speed must be 0 to 500.",
            },
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      SaurerTC2: {
         value: 10,
         label: "Saurer Alma Technocorder TC2",
         elements: [
            _.assign(
               {
                  maxLength: 8,
                  isRequired: () => { return true; },
               },
               commonElem.PROGRAM_TEXT
            ),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.CLOCK_SET_SIX),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 15000),
                  errorMessage: "Speed Setting must be 0 to 15,000.",
               },
               commonElem.SPEED
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9),
                  errorMessage: "Number of Ends must be 1 to 9.",
               },
               commonElem.NUMBER_OF_ENDS
            ),
            _.assign(
               { maxLength: 16, },
               commonElem.TUBE_COLOR
            ),
            _.assign({}, commonElem.TENSION_SET),
            _.assign({}, commonElem.BALLOON_GUIDE),
            _.assign({}, commonElem.SPINDLE_WRAPS),
            _.assign({}, commonElem.POT_LIDS),
            _.assign({}, commonElem.CAPSTAN_WRAPS_SIX),
            _.assign({}, commonElem.TENSION_DEVICE),
            _.assign({}, commonElem.BULLET_SIZE),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      SSMDuro: {
         value: 38,
         label: "SSM Duro",
         elements: [
            _.assign({}, commonElem.PROGRAM_NAME),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.TUBE_OD),
            _.assign({}, commonElem.YARDS_PER_PACKAGE),
            _.assign(
               {
                  fields: [
                     { label: "Start", field: "speedStart" },
                     { label: "Middle", field: "speedMiddle" },
                     { label: "End", field: "speedEnd" },
                  ],
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 50, 9999),
                  errorMessage: "Speed values must be 50 to 9,999.",
               },
               commonElem.MULTI_SPEED
            ),
            _.assign({}, commonElem.TRAVERSE),
            _.assign({}, commonElem.LEFT_TAPER),
            _.assign({}, commonElem.LEFT_COMPENSATION),
            _.assign({}, commonElem.RIGHT_TAPER),
            _.assign({}, commonElem.RIGHT_COMPENSATION),
            _.assign({}, commonElem.BACK_PRESSURE_BEGIN_END),
            _.assign({}, commonElem.GRAF),
            _.assign({}, commonElem.WIND_TYPE),
            editItem?.windType === digiwind
               ? _.assign({}, commonElem.DEGREES)
               : _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.WINDING_TENSION),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      SSMKing: {
         value: 31,
         label: "SSM TK2-20KE Kingspool Winder",
         elements: [
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9999),
                  errorMessage: "Program # must be 1 to 9,999.",
               },
               commonElem.PROGRAM_NUMBER
            ),
            _.assign({}, commonElem.RUNTIME),
            {
               label: "Meters Per Package (m)",
               dataType: constants.DATA_TYPES.INT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "metersPerPkg",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9999999),
               errorMessage: "Meters Per Package must be between 1 and 9,999,999",
            },
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 6000),
                  errorMessage: "Back Pressure must be 1 to 6,000.",
               },
               commonElem.BACK_PRESSURE_BEGIN_END
            ),
            _.assign({}, commonElem.GRAF),
            editItem?.windType === digiwind
               ? _.assign({}, commonElem.DEGREES)
               : _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      TexKing: {
         value: 9,
         label: "Texilmesa KS Winder",
         elements: [
            _.assign({}, commonElem.CLOCK_SET_SIX),
            _.assign({}, commonElem.RUNTIME),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 700),
                  errorMessage: "Speed Setting must be 0 to 700.",
               },
               commonElem.SPEED_SET
            ),
            _.assign({}, commonElem.GRAF),
            {
               label: "Tension Disk (g)",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.MINMAX,
               minField: "tensionMin",
               maxField: "tensionMax",
               minLabel: "Min",
               maxLabel: "Max",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 300),
               errorMessage: "Gainer Gear must be 0 to 300.",
            },

            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Taining35AFP: {
         value: 11,
         label: "Taining 35AFP Winder",
         elements: [
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 100),
                  isRequired: () => { return true; },
                  errorMessage: "Program # must be 1 to 100.",
               },
               commonElem.PROGRAM_NUMBER
            ),
            _.assign({}, commonElem.YARN_LENGTH_CLOCK_SETTING),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.SPECIFICATION),
            _.assign({}, commonElem.ROLLER_SPEED),
            _.assign({}, commonElem.YARN_TENSIONER),
            _.assign({}, commonElem.YARN_TENSIONER_LI),
            _.assign({}, commonElem.SPACING),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Taining35AR: {
         value: 12,
         label: "Taining 35AR Winder",
         elements: [
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 100),
                  isRequired: () => { return true; },
                  errorMessage: "Program # must be 1 to 100.",
               },
               commonElem.PROGRAM_NUMBER
            ),
            _.assign({}, commonElem.YARN_LENGTH_CLOCK_SETTING),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.SPECIFICATION),
            _.assign({}, commonElem.ROLLER_SPEED),
            _.assign({}, commonElem.YARN_TENSIONER),
            _.assign({}, commonElem.YARN_TENSIONER_LI),
            _.assign({}, commonElem.SPACING),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Taining35ATube: {
         value: 22,
         label: "Taining 35A Tube Winder",
         elements: [
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 100),
                  isRequired: () => { return true; },
                  errorMessage: "Program # must be 1 to 100.",
               },
               commonElem.PROGRAM_NUMBER
            ),
            _.assign({}, commonElem.YARN_LENGTH_CLOCK_SETTING),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.SPECIFICATION),
            _.assign({}, commonElem.ROLLER_SPEED),
            _.assign({}, commonElem.YARN_TENSIONER),
            _.assign({}, commonElem.YARN_TENSIONER_LI),
            _.assign({}, commonElem.SPACING),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      TainingS41: {
         value: 28,
         label: "Taining TN-09S S41 Winder",
         elements: [
            _.assign({}, commonElem.IMPULSE_SET),
            _.assign({}, commonElem.RUNTIME),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 600, 3000),
                  errorMessage: "Delivery Speed must be 600 to 3,000.",
               },
               commonElem.SPEED
            ),
            _.assign({}, commonElem.LUBE_ROLLER),
            _.assign({}, commonElem.TENSION),
            {
               label: "Underwind B (sec)",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "underwindB",
               dataType: constants.DATA_TYPES.SMALLINT,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 11),
               errorMessage: "Underwind B must be 0 to 11.",
            },
            {
               label: "Underwind T (sec)",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "underwindT",
               dataType: constants.DATA_TYPES.SMALLINT,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 5),
               errorMessage: "Underwind T must be 0 to 5.",
            },
            {
               label: "Underwind D (sec)",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "underwindD",
               dataType: constants.DATA_TYPES.SMALLINT,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 11),
               errorMessage: "Underwind D must be 0 to 5.",
            },
            {
               label: "Underwind K (sec)",
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "underwindK",
               dataType: constants.DATA_TYPES.SMALLINT,
               validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 5),
               errorMessage: "Underwind K must be 0 to 5.",
            },
            _.assign({}, commonElem.LAY_WHEEL_SET),
            _.assign({}, commonElem.TENSION_POST_SET),
            _.assign({}, commonElem.BACK_PRESSURE_SPRING),
            _.assign({}, commonElem.FEEDER_TENSION_SET),
            _.assign({}, commonElem.FEEDER_WRAPS),
            _.assign({}, commonElem.YARDS),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      UT50: {
         value: 15,
         label: "UT50/60-733 Filament Twister",
         elements: [
            _.assign({}, commonElem.CLOCK_SET),
            _.assign({}, commonElem.RUNTIME),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 99999),
                  isRequired: () => { return true; },
                  errorMessage: "Program # must be 1 to 99,999.",
               },
               commonElem.PROGRAM_NUMBER
            ),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 6),
                  errorMessage: "Number of Ends must be 0 to 6.",
               },
               commonElem.NUMBER_OF_ENDS
            ),
            _.assign(
               { maxLength: 30, },
               commonElem.TUBE_COLOR
            ),
            _.assign({}, commonElem.TENSION_DEVICE),
            editItem && editItem.tensionDevice === yarnTensioner
               ? {
                  label: "Spring #",
                  dataType: constants.DATA_TYPES.SMALLINT,
                  type: ELEMENT_TYPES.DEBOUNCE,
                  field: "springNum",
                  errorMessage: "Yarn Tensioner must also have a spring # between 0 and 10.",
                  validate: (value, field, item) => {
                     // this only applies if the tension device is set to Yarn Tensioner
                     if (item.tensionDevice === yarnTensioner) {
                        return rgx.validateIntegerRange(value, field, 0, 10);
                     } else {
                        return true;
                     }
                  },
                  isRequired: (item) => { return item.tensionDevice === yarnTensioner; },
                  subtitle: "Only applies to Yarn Tensioner",
                  modify: (field, value, index, onChangeCallback, editItem) => {
                     if (editItem["tensionDevice"] === yarnTensioner) {
                        onChangeCallback(field, value, index);
                     }
                  },
               }
               : null,
            _.assign({}, commonElem.TENSION_SET),
            _.assign({}, commonElem.BALLOON_GUIDE),
            {
               label: "Reserve Disk (mm)",
               field: "reserveDisk",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.RADIO,
               options: [
                  { label: "160", value: 160 },
                  { label: "200", value: 200 },
               ],
               required: () => { return true; },
            },
            _.assign({}, commonElem.SPINDLE_WRAPS),
            _.assign({}, commonElem.POT_LIDS),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 10500),
                  errorMessage: "Speed Setting must be 0 to 10,500.",
               },
               commonElem.SPEED
            ),
            _.assign({}, commonElem.CAPSTAN_WRAPS_SIX),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Xeno: {
         value: 36,
         label: "SSM Xeno Winder",
         elements: [
            _.assign({}, commonElem.PROGRAM_NAME),
            _.assign({}, commonElem.RUNTIME),
            _.assign({}, commonElem.TUBE_OD),
            _.assign({}, commonElem.YARDS_PER_PACKAGE),
            _.assign(
               {
                  fields: [
                     { label: "Initial", field: "initialSpeed" },
                     { label: "Production", field: "productionSpeed" },
                     { label: "Decel", field: "decelSpeed" },
                  ],
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 50, 9999),
                  errorMessage: "Speed values must be 50 to 9,999.",
               },
               commonElem.MULTI_SPEED
            ),
            _.assign({}, commonElem.TRAVERSE),
            _.assign({}, commonElem.LEFT_TAPER),
            _.assign({}, commonElem.LEFT_COMPENSATION),
            _.assign({}, commonElem.RIGHT_TAPER),
            _.assign({}, commonElem.RIGHT_COMPENSATION),
            _.assign(
               {
                  validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 9999),
                  errorMessage: "Back Pressure must be 1 to 9,999.",
               },
               commonElem.BACK_PRESSURE_BEGIN_END
            ),
            _.assign({}, commonElem.WIND_TYPE),
            editItem?.windType === digiwind
               ? _.assign({}, commonElem.DEGREES)
               : _.assign({}, commonElem.WIND_RATIO),
            _.assign({}, commonElem.WINDING_TENSION),
            _.assign({}, commonElem.CAPSTAN_WRAPS_SEVEN),
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      Xorella: {
         value: 37,
         label: "Xorella Conditioner",
         elements: [
            _.assign({}, commonElem.RUNTIME),
            {
               label: "Cycle Count",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "cycleCount",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 10),
               errorMessage: "Cycle Count must be 1 to 10.",
            },
            _.assign(
               { maxLength: 20, },
               commonElem.PROGRAM_TEXT
            ),
            {
               label: null,
               type: ELEMENT_TYPES.MULTI_GRID,
               fields: [
                  {
                     label: "#1 Min (HH:MM:SS)",
                     field: "minTime1",
                     dataType: constants.DATA_TYPES.TEXT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     maxLength: 10,
                     isRequired: () => { return true; },
                     validate: (value, field) => rgx.validateHMS(value, field),
                     errorMessage: "#1 Min must be in HH:MM:SS format",
                  },
                  {
                     label: "#2 Min (HH:MM:SS)",
                     field: "minTime2",
                     dataType: constants.DATA_TYPES.TEXT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     maxLength: 10,
                     isRequired: () => { return true; },
                     validate: (value, field) => rgx.validateHMS(value, field),
                     errorMessage: "#2 Min must be in HH:MM:SS format",
                  },
                  {
                     label: "#3 Min (HH:MM:SS)",
                     field: "minTime3",
                     dataType: constants.DATA_TYPES.TEXT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     maxLength: 10,
                     isRequired: () => { return true; },
                     validate: (value, field) => rgx.validateHMS(value, field),
                     errorMessage: "#3 Min must be in HH:MM:SS format",
                  },
                  {
                     label: "#1 Mbar",
                     field: "mBar1",
                     dataType: constants.DATA_TYPES.SMALLINT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999),
                     errorMessage: "#1 Mbar must be 0 to 999.",
                  },
                  {
                     label: "#2 Mbar",
                     field: "mBar2",
                     dataType: constants.DATA_TYPES.SMALLINT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999),
                     errorMessage: "#2 Mbar must be 0 to 999.",
                  },
                  {
                     label: "#3 Min Mbar",
                     field: "mBar3",
                     dataType: constants.DATA_TYPES.SMALLINT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateIntegerRange(value, field, 0, 999),
                     errorMessage: "#3 Mbar must be 0 to 999.",
                  },
                  {
                     label: "#1 °C",
                     field: "celsius1",
                     dataType: constants.DATA_TYPES.FLOAT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 99.9, false, true),
                     errorMessage: "#1 °C must be 0.0 to 99.9.",
                  },
                  {
                     label: "#2 °C",
                     field: "celsius2",
                     dataType: constants.DATA_TYPES.FLOAT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 99.9, false, true),
                     errorMessage: "#2 °C must be 0.0 to 99.9.",
                  },
                  {
                     label: "#3 °C",
                     field: "celsius3",
                     dataType: constants.DATA_TYPES.FLOAT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 99.9, false, true),
                     errorMessage: "#3 °C must be 0.0 to 99.9.",
                  },
                  {
                     label: "#1 °C/min",
                     field: "celsiusMin1",
                     dataType: constants.DATA_TYPES.FLOAT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 9.9, false, true),
                     errorMessage: "#1 °C/min must be 0.0 to 9.9.",
                  },
                  {
                     label: "#2 °C/min",
                     field: "celsiusMin2",
                     dataType: constants.DATA_TYPES.FLOAT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 9.9, false, true),
                     errorMessage: "#2 °C/min must be 0.0 to 9.9.",
                  },
                  {
                     label: "#3 °C/min",
                     field: "celsiusMin3",
                     dataType: constants.DATA_TYPES.FLOAT,
                     type: ELEMENT_TYPES.DEBOUNCE,
                     validate: (value, field) => rgx.validateDecimalRange(value, field, 2, 1, 0.0, 9.9, false, true),
                     errorMessage: "#3 °C/min must be 0.0 to 9.9.",
                  },
               ],
            },
            {
               label: "Yield Pounds",
               dataType: constants.DATA_TYPES.SMALLINT,
               type: ELEMENT_TYPES.DEBOUNCE,
               field: "pounds",
               validate: (value, field) => rgx.validateIntegerRange(value, field, 1, 3300),
               errorMessage: "Yield Pounds must be 1 to 3,300.",
            },
            _.assign({}, commonElem.STD_EFFICIENCY),
            _.assign({}, commonElem.STD_SPINDLE_COUNT),
            _.assign({}, commonElem.LABOR_PCT),
         ],
      },
      // BOB
   };
   if (!machineType) {
      return _.toArray(MachineTypes);
   }
   return _.find(_.toArray(MachineTypes), (s) => {
      return s.value === machineType;
   });
};

function MachineSetupArea(props) {
   const { index, specInfo, setSpecInfo, editItem } = props;
   let config = getMachineTypes(props.calcType, props.machineType, editItem);
   if (config) {
      return config.elements?.map((elem, idx) => {
         if (!elem) return null;
         let element;
         const {
            field,
            label,
            type,
            maxLength,
            fields,
            options,
            minLabel,
            maxLabel,
            minField,
            maxField,
            modify,
            subtitle,
            trueLabel,
            falseLabel,
            sideLabel,
            colSize,
            showBreak,
         } = elem;
         if (showBreak) return <div className="minHeight38 mb-1" />;
         let value = props[field];

         // const maxChars = options && _.chain(options).map((opt) => opt.label?.length).max().value();
         function DebounceElement({
            debounceValue,
            debounceField,
            debounceMaxLength,
            debounceSubtitle,
            debounceSidelabel,
         }) {
            return (
               <Col xs={9} className="height38">
                  <DebounceInput
                     value={debounceValue}
                     onChange={(e) =>
                        props.onChange(debounceField, e.target.value, index)
                     }
                     maxLength={debounceMaxLength}
                     debounceTimeout={1000}
                     className="form-control width115px"
                  />
                  {debounceSidelabel && (
                     <div className="debounceSidelabel">{debounceSidelabel}</div>
                  )}
                  {debounceSubtitle && (
                     <i className="fontSize85">{debounceSubtitle}</i>
                  )}
               </Col>
            );
         }
         function RadioElement({ radioValue, radioField, radioSubtitle, radioOptions, }) {
            return (
               <Col>
                  <Row className="w-100">
                     {_.map(radioOptions, (opt) => {
                        const onClick = () =>
                           modify
                              ? modify(
                                 radioField,
                                 opt.value,
                                 index,
                                 props.onChange,
                                 editItem,
                                 setSpecInfo,
                                 specInfo
                              )
                              : props.onChange(radioField, opt.value, index);

                        let labelLength = opt.label.length;
                        return (
                           <Col
                              onClick={onClick}
                              className={classnames(
                                 {
                                    width95px: labelLength <= 4,
                                    width115px: labelLength > 4 && labelLength < 8,
                                    width135px: labelLength >= 8,
                                 },
                                 "minHeight38 cursor-pointer pt-2 pe-1"
                              )}
                              key={`radioopt-${opt.value}${idx}`}
                           >
                              <Input
                                 type="radio"
                                 className="me-1 cursor-pointer"
                                 checked={radioValue === opt.value}
                                 onChange={onClick}
                              />
                              {opt.label}
                           </Col>
                        );
                     })}
                  </Row>
                  {radioSubtitle && <i className="fontSize85">{radioSubtitle}</i>}
               </Col>
            );
         }
         switch (type) {
            case ELEMENT_TYPES.DEBOUNCE:
               element = (
                  <DebounceElement
                     debounceValue={value}
                     debounceField={field}
                     debounceMaxLength={maxLength}
                     debounceSubtitle={subtitle}
                     debounceSidelabel={sideLabel}
                  />
               );
               break;
            case ELEMENT_TYPES.SINGLE_SELECT:
               element = (
                  <Col xs={4}>
                     <Select
                        value={_.find(options, (i) => i.value === value)}
                        onChange={(e) => props.onChange(field, e.value, index)}
                        options={options}
                        makeAnimated={makeAnimated}
                        classNamePrefix="serviceThreadSelect"
                     />
                  </Col>
               );
               break;
            case ELEMENT_TYPES.SWITCH:
               element = (
                  <Col xs={9}>
                     <Switch
                        onChange={(checked) => props.onChange(field, checked, index)}
                        checked={value || false}
                     />
                  </Col>
               );
               break;
            case ELEMENT_TYPES.RADIO:
               element = (
                  <RadioElement
                     radioValue={value}
                     radioField={field}
                     radioSubtitle={subtitle}
                     radioOptions={options}
                  />
               );
               break;
            case ELEMENT_TYPES.MULTI_GRID:
               element = (
                  <Row key={`multi_Grid${idx}`} className="mb-1">
                     {_.map(fields, (opt, optIndex) => {
                        return (
                           <Fragment key={`multi-grid-${opt.field || optIndex}${idx}`}>
                              {opt.type === constants.DATA_TYPES.HEADING ? (
                                 <HeaderColumn
                                    label={opt.label}
                                    colWidth={colSize * 2 || 4}
                                    underline={true}
                                    className="mb-1"
                                 />
                              ) : (
                                 <>
                                    <HeaderColumn
                                       label={opt.label}
                                       colWidth={colSize || 2}
                                       className="mb-1"
                                    />
                                    <Col xs={colSize || 2} className="mb-1">
                                       {opt.type === ELEMENT_TYPES.DEBOUNCE && (
                                          <DebounceElement
                                             debounceValue={editItem[opt.field]}
                                             debounceField={opt.field}
                                             debounceMaxLength={maxLength}
                                             debounceSubtitle={subtitle}
                                             debounceSidelabel={opt.sideLabel}
                                          />
                                       )}
                                       {opt.type === ELEMENT_TYPES.RADIO && (
                                          <RadioElement
                                             radioValue={editItem[opt.field]}
                                             radioField={opt.field}
                                             radioOptions={opt.options}
                                          />
                                       )}
                                    </Col>
                                 </>
                              )}
                           </Fragment>
                        );
                     })}
                  </Row>
               );
               break;
            case ELEMENT_TYPES.MULTI_INPUT:
               element = (
                  <Col xs={9}>
                     <Row>
                        {_.map(fields, (opt) => {
                           let labelLength = opt.label.length;
                           return (
                              <Col
                                 className={classnames(
                                    {
                                       width185px: labelLength <= 4,
                                       width225px: labelLength > 4 && labelLength < 8,
                                       width265px: labelLength >= 8,
                                    },
                                    "minHeight38"
                                 )}
                                 key={`multi-input-${opt.field}${idx}`}
                              >
                                 <Row>
                                    <Col xs="5" className="fontSize92">
                                       {opt.label}
                                    </Col>
                                    <Col xs="7" className="width115px">
                                       <DebounceInput
                                          value={props[opt.field]}
                                          onChange={(e) =>
                                             props.onChange(opt.field, e.target.value, index)
                                          }
                                          debounceTimeout={300}
                                          className="form-control width115px"
                                       />
                                    </Col>
                                 </Row>
                              </Col>
                           );
                        })}
                     </Row>
                  </Col>
               );
               break;
            case ELEMENT_TYPES.MULTI_BOOL:
               element = (
                  <Col xs={9}>
                     <Row>
                        {_.map(fields, (opt) => {
                           value = props[opt.field];
                           const onClick = (boolValue) =>
                              modify
                                 ? modify(
                                    opt.field,
                                    boolValue,
                                    index,
                                    props.onChange,
                                    editItem,
                                    setSpecInfo,
                                    specInfo
                                 )
                                 : props.onChange(opt.field, boolValue, index);
                           return (
                              <>
                                 <Col xs={1}>
                                    <h5
                                       children={opt.label}
                                       className="float-end fontSize92 pt-2"
                                       key={`multi-bool-${opt.field}${idx}`}
                                    />
                                 </Col>
                                 <Col xs="3" className="ps-4 pe-0 align-self-center">
                                    <Row className="width115px">
                                       <Col
                                          className="px-0 align-self-center minHeight38 cursor-pointer"
                                          onClick={() => onClick(true)}
                                       >
                                          <Input
                                             type="radio"
                                             className="me-1 cursor-pointer"
                                             checked={value === true}
                                             onChange={() => onClick(true)}
                                          />
                                          {trueLabel}
                                       </Col>
                                       <Col
                                          className="px-0 align-self-center minHeight38 cursor-pointer"
                                          onClick={() => onClick(false)}
                                       >
                                          <Input
                                             type="radio"
                                             className="me-1 cursor-pointer"
                                             checked={value === false}
                                             onChange={() => onClick(false)}
                                          />
                                          {falseLabel}
                                       </Col>
                                    </Row>
                                 </Col>
                              </>
                           );
                        })}
                     </Row>
                  </Col>
               );
               break;
            case ELEMENT_TYPES.TRAVELER_SIZE:
               let sizes = [];
               _.mapKeys(editItem.travelerOptions, (value, key) => {
                  sizes.push({
                     label: value,
                     field: key,
                  });
               });

               element = (
                  <Col xs={9}>
                     <Row className="pt-3">
                        {_.map(sizes, (opt, idx) => {
                           let boolField = `traveler${opt.field}`;
                           let qtyField = `travelerQty${opt.field}`;
                           let boolValue = props[boolField];
                           const onClick = () => {
                              modify
                                 ? modify(
                                    boolField,
                                    !boolValue,
                                    index,
                                    props.onChange,
                                    editItem,
                                    setSpecInfo,
                                    specInfo
                                 )
                                 : props.onChange(boolField, !boolValue, index);
                           };
                           return (
                              <Col
                                 className="minHeight38 mb-2"
                                 key={`traveler-size-${opt.field}${idx}`}
                                 xs="8"
                              >
                                 <Row>
                                    <Col
                                       xs="6"
                                       className="fontSize92 font-weight-normal cursor-pointer"
                                       onClick={onClick}
                                    >
                                       <Input
                                          type="checkbox"
                                          className="me-3 cursor-pointer"
                                          checked={boolValue === true}
                                          onChange={onClick}
                                       />
                                       {opt.label}
                                    </Col>
                                    <Col xs="6" className="width115px">
                                       {boolValue && (
                                          <DebounceInput
                                             value={props[qtyField] || ""}
                                             onChange={(e) => {
                                                props.onChange(qtyField, e.target.value, index);
                                             }}
                                             debounceTimeout={300}
                                             className="form-control width115px"
                                          />
                                       )}
                                    </Col>
                                 </Row>
                              </Col>
                           );
                        })}
                     </Row>
                  </Col>
               );
               break;
            case ELEMENT_TYPES.MINMAX:
               element = (
                  <Col xs={9}>
                     <Row className="w-100">
                        <Col xs={minLabel.length > 5 ? 2 : 1}>
                           <h5
                              children={minLabel}
                              className="float-start fontSize92 pt-2"
                           />
                        </Col>
                        <Col xs={3}>
                           <DebounceInput
                              value={props[minField]}
                              onChange={(e) =>
                                 props.onChange(minField, e.target.value, index)
                              }
                              maxLength={maxLength}
                              debounceTimeout={300}
                              className="form-control width115px"
                           />
                        </Col>
                        <Col xs={1}>
                           <h5
                              children={maxLabel}
                              className="float-end fontSize92 pt-2"
                           />
                        </Col>
                        <Col xs={3} className="pe-4">
                           <DebounceInput
                              value={props[maxField]}
                              onChange={(e) =>
                                 props.onChange(maxField, e.target.value, index)
                              }
                              maxLength={maxLength}
                              debounceTimeout={300}
                              className="form-control width115px"
                           />
                        </Col>
                     </Row>
                  </Col>
               );
               break;
            default:
               break;
         }
         if (type === ELEMENT_TYPES.MULTI_GRID) {
            return element;
         }
         return (
            <Row key={`detailRow${idx}`} className="mb-1">
               <HeaderColumn label={label} colWidth={3} />
               {element}
            </Row>
         );
      });
   }
   return null;
}

function modifyTraveler(field, value, index, onChangeCallback, editItem, setSpecInfo, specInfo) {
   let boolField = _.includes(field, "Qty")
      ? _.replace(field, "travelerQty", "traveler")
      : field;
   let qtyField = _.includes(field, "Qty")
      ? field
      : _.replace(field, "traveler", "travelerQty");
   if (value) {
      if (_.isBoolean(value)) {
         onChangeCallback(field, value, index);
      } else if (editItem[boolField] === true) {
         onChangeCallback(field, value, index);
      } else {
         onChangeCallback(field, "", index);
      }
   } else {
      let newList = specInfo.machineSetupList.slice();
      let changes = _.assign({}, newList[index]);
      changes[field] = value;
      changes[qtyField] = null;
      newList[index] = changes;
      let newInfo = _.assign({}, specInfo);
      newInfo.machineSetupList = newList;
      setSpecInfo(newInfo);
   }
}

function shapeExtraFields(payload, item) {
   let config = getMachineTypes(item.calcType, item.machineType, item);
   let warnings = [];
   let extraData = {};
   if (config) {
      _.map(config.elements, (elem) => {
         let {
            field,
            label,
            dataType,
            maxLength,
            minLabel,
            maxLabel,
            validate,
            minField,
            maxField,
            isRequired,
            errorMessage,
            fields,
            disregardMinMax = false,
            showBreak,
         } = elem;
         if (!showBreak) {
            let required = isRequired ? isRequired(item) : false;
            function validateFields(
               fieldToValidate,
               fieldDataType,
               fieldValidate,
               fieldErrorMessage,
               fieldLabel,
               fieldMaxLength
            ) {
               let value = item[fieldToValidate];
               let cSharpField = `${_.upperFirst(_.camelCase(fieldToValidate))}`;
               // these do not work with the case logic above
               if (cSharpField === "ApplicatorRollRpm") {
                  cSharpField = "ApplicatorRollRPM";
               }
               if (cSharpField === "CounterFc71") {
                  cSharpField = "CounterFC71";
               }
               if (cSharpField === "CounterFc72") {
                  cSharpField = "CounterFC72";
               }
               if (cSharpField === "TensionLi") {
                  cSharpField = "TensionLI";
               }
               if (cSharpField === "TubeOd") {
                  cSharpField = "TubeOD";
               }
               if (required && !item[fieldToValidate]) {
                  warnings.push(errorMessage);
               } else {
                  let validated = true;
                  if (validate) {
                     validated = validate(value, fieldToValidate, item);
                     if ((required || item[fieldToValidate]) && !validated) {
                        warnings.push(errorMessage);
                     }
                  }
                  if (fieldValidate) {
                     validated = fieldValidate(value, fieldToValidate, item);
                     if ((required || item[fieldToValidate]) && !validated) {
                        warnings.push(fieldErrorMessage);
                     }
                  }
                  if (validated) {
                     if (fieldDataType) {
                        dataType = fieldDataType;
                     }
                     if (cSharpField.length > 0) {
                        // a few elements are just containers for specifically, multi-grid,
                        // underlying fields, so do not need to be validated themselves
                        // or added to the payload
                        switch (dataType) {
                           case constants.DATA_TYPES.TEXT:
                              if (!validateText(value, maxLength || fieldMaxLength)) {
                                 warnings.push(`Please limit ${label || fieldLabel} to ${maxLength || fieldMaxLength}\n`);
                                 validated = false;
                              } else {
                                 extraData[cSharpField] = value === null ? value : value;
                              }
                              break;
                           case constants.DATA_TYPES.SMALLINT:
                           case constants.DATA_TYPES.INT:
                              extraData[cSharpField] =
                                 value === null
                                    ? value
                                    : parseInt(_.replace(value, /,/g, ""), 10);
                              break;
                           case constants.DATA_TYPES.DOUBLE:
                           case constants.DATA_TYPES.DECIMAL:
                           case constants.DATA_TYPES.FLOAT:
                              extraData[cSharpField] =
                                 value === null
                                    ? value
                                    : formatValues(
                                       fieldToValidate,
                                       _.replace(value, /,/g, ""),
                                       item.machineType
                                    );
                              break;
                           default:
                              extraData[cSharpField] = value;
                              break;
                        }
                     }
                  }
               }
            }
            if (field) {
               validateFields(field);
            } else if (fields) {
               _.forEach(fields, (f) => {
                  if (!f.showBreak) {
                     validateFields(
                        f.field,
                        f.dataType,
                        f.validate,
                        f.errorMessage,
                        f.label,
                        f.maxLength
                     );
                  }
               });
            } else if (minField || maxField) {
               if (required && (!item[minField] || !item[maxField])) {
                  warnings.push(errorMessage);
               } else {
                  let validated = true;
                  let minValue = item[minField];
                  let maxValue = item[maxField];
                  let cSharpMinField = `${_.upperFirst(_.camelCase(minField))}`;
                  let cSharpMaxField = `${_.upperFirst(_.camelCase(maxField))}`;

                  if (cSharpMinField.length > 0 && cSharpMaxField.length > 0) {
                     // a few elements are just containers for specifically, multi-grid,
                     // underlying fields, so do not need to be validated themselves
                     // or added to the payload
                     if (validate) {
                        let validMin = validate(minValue, minField, item);
                        let validMax = validate(maxValue, maxField, item);
                        validated = validMin && validMax;
                        let minMaxFail = maxValue < minValue;
                        if (minMaxFail) {
                           minMaxFail = parseFloat(maxValue) < parseFloat(minValue);
                        }
                        // disregardMinMax indicates to allow these two values to be unrelated
                        if (!disregardMinMax && minMaxFail) {
                           warnings.push(`${label} (${maxLabel}) must be greater than (${minLabel})\n`);
                        } else {
                           if (!validated) {
                              warnings.push(errorMessage);
                           }
                        }
                     }
                     if (validated) {
                        switch (dataType) {
                           case constants.DATA_TYPES.SMALLINT:
                           case constants.DATA_TYPES.INT:
                              extraData[cSharpMinField] = parseInt(
                                 _.replace(minValue, /,/g, ""),
                                 10
                              );
                              extraData[cSharpMaxField] = parseInt(
                                 _.replace(maxValue, /,/g, ""),
                                 10
                              );
                              break;
                           case constants.DATA_TYPES.DECIMAL:
                           case constants.DATA_TYPES.FLOAT:
                              extraData[cSharpMinField] = formatValues(
                                 minField,
                                 _.replace(minValue, /,/g, ""),
                                 item.machineType
                              );
                              extraData[cSharpMaxField] = formatValues(
                                 maxField,
                                 _.replace(maxValue, /,/g, ""),
                                 item.machineType
                              );
                              break;
                           default:
                              extraData[cSharpMinField] = minValue;
                              extraData[cSharpMaxField] = maxValue;
                              break;
                        }
                     }
                  }
               }
            }
         }
      });
   }
   if (_.keys(extraData)?.length > 0) {
      payload.ExtendedPropertyJson = extraData;
   }
   return {
      newPayload: payload,
      moreWarnings: warnings,
   };
}

function validateBoolean(value, field) {
   return _.isBoolean(value);
}

function validateText(value, maxLength) {
   return value && value.length < maxLength;
}

function validateTraveler(value, field, item) {
   let boolField = _.includes(field, "Qty")
      ? _.replace(field, "travelerQty", "traveler")
      : field;
   let qtyField = _.includes(field, "Qty")
      ? field
      : _.replace(field, "traveler", "travelerQty");
   let boolValue = item[boolField];
   let qtyValue = item[qtyField];
   if (boolValue) {
      return _.includes([1, 2, 3, "1", "2", "3"], qtyValue);
   } else {
      return true;
   }
}

export default function MachineSetup(props) {
   const { currentUser } = useContext(UserContext);
   const canActivate = currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.canActivateMachineSetups;
   const canDelete = currentUser?.isAdmin || currentUser?.isPowerUser;
   const canEdit =
      currentUser?.isAdmin ||
      currentUser?.isPowerUser ||
      helpers.hasRoleId(currentUser, constants.ROLE_IDS.EngineeringEdit);

   const { machineSetupSelect } = useParams();
   const location = useLocation();
   const [copying, setCopying] = useState(false);
   const [copyToItemOptions, setCopyToItemOptions] = useState([]);
   const [downloading, setDownloading] = useState(false);
   const [itemOptions, setItemOptions] = useState([]);
   const [itemToCopy, setItemToCopy] = useState(null);
   const [itemToDelete, setItemToDelete] = useState(null);
   const [loading, setLoading] = useState(false);
   const [machineTypes, setMachineTypes] = useState([]);
   const [message, setMessage] = useState(null);
   const [printConfiguration, setPrintConfiguration] = useState(false);
   const [printRevisionNotes, setPrintRevisionNotes] = useState(false);
   const [saving, setSaving] = useState(false);
   const [selectedItem, setSelectedItem] = useState(null);
   const [specInfo, setSpecInfo] = useState({});
   const emptyNewItem = {
      machineType: constants.MACHINE_SETUP.OTHER,
      instructions: "",
      orderToProcess: 0,
      isActive: true,
   };
   let calculationOptions = [];
   let cumulativeSetup = constants.CALCULATION_TYPES.CUMULATIVE;
   let editItem = specInfo && _.find(specInfo.machineSetupList, (item) => { return item.editing; });
   let editItemIndex = editItem && _.findIndex(specInfo?.machineSetupList, (l) => { return l.setupId === editItem.setupId; });
   let soleSetup = constants.CALCULATION_TYPES.SOLE_SETUP;

   useEffect(() => {
      document.title = `Machine Setups`;
      if (_.isNil(machineSetupSelect)) {
         return;
      } else {
         const json = decodeURI(machineSetupSelect);
         const opt = JSON.parse(json);
         setSelectedItem(opt);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location]);

   useEffect(() => {
      document.title = `Machine Setups`;
      let apiCalls = [];
      apiCalls.push(
         api
            .fetch("Reference/GetMachineTypes")
            .then((response) => {
               return {
                  machineTypeList: _.chain(response.data?.list)
                     .filter((m) => {
                        return _.some(getMachineTypes(), (mt) => mt.value === m.id);
                     })
                     .map((m) => {
                        m.value = m.id;
                        m.label = m.name;
                        return m;
                     })
                     .sortBy((m) => {
                        return m.label;
                     })
                     .value(),
               };
            })
            .catch(api.catchHandler)
      );
      apiCalls.push(
         api
            .fetch("Item/GetSpecItemsWithSamples", { searchString: "" })
            .then((response) => {
               return {
                  itemDropdownList: _.map(response.data.list, (d) => {
                     d.value = d.itemNumber;
                     // need all the values for other
                     return d;
                  }),
               };
            })
            .catch(api.catchHandler)
      );
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            setMachineTypes(
               aggResults.machineTypeList ? aggResults.machineTypeList : []
            );
            if (
               aggResults.itemDropdownList &&
               aggResults.itemDropdownList.length > 0
            ) {
               const origList = aggResults.itemDropdownList;
               const fullLabelList = _.map(origList, (d) => {
                  let newItem = _.assign({}, d);
                  newItem.label = d.itemWithDesc;
                  return newItem;
               });
               const shortHandLabelList = _.map(origList, (d) => {
                  let newItem = _.assign({}, d);
                  newItem.label = d.itemNumber;
                  return newItem;
               });
               setItemOptions(fullLabelList);
               setCopyToItemOptions(shortHandLabelList);
            }
         })
         .catch((error) =>
            toast.warning("There was an error loading the drop down list data")
         );
   }, []);

   useEffect(() => {
      document.title = `Machine Setups`;
      // clear machine setup specs if cleared out the selected item
      if (!selectedItem && specInfo) {
         setSpecInfo(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedItem]);

   useEffect(() => {
      document.title = `Machine Setups`;
      if (
         selectedItem &&
         (!specInfo || specInfo.value !== selectedItem.value) &&
         !loading
      ) { fetchData(false); }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedItem]);

   function addNewSetup() {
      let changes = _.assign({}, emptyNewItem);
      changes.itemNumber = specInfo.itemNumber;
      let activeSetups = _.filter(specInfo.machineSetupList, (m) => {
         return m.isActive;
      });
      if (
         specInfo.machineSetupList?.length < 1 ||
         !activeSetups ||
         activeSetups.length < 1
      ) {
         //  The machine setup list is void or has no items, so no setups yet exist which means this is the sole setup
         // Sole Setup - this is the 1st and only setup to have been created, or all the
         // existing setups are inactive and this will become the only active setup
         changes.calcType = constants.CALCULATION_TYPES.SOLE_SETUP.value;
      } else if (activeSetups.length === 1) {
         // This is going to be the 2nd active setup to be created. The calc type for a setup group is not known yet.
         // Default to "unknown" type and let the user pick one on edit.
         changes.calcType = constants.CALCULATION_TYPES.UNKNOWN.value;
      } else {
         //Two or more active setups already exist, so make this one the same calc type as the others.
         changes.calcType = activeSetups[0].calcType;
      }
      saveMachineSetup(changes);
   }

   function copyItemSetup(item) {
      let copyThis = item.copyToItem;
      setCopying(true);
      api
         .fetch(`MachineSetup/CheckForDuplicateMachineSetup`, {
            ItemNumber: copyThis.itemNumber,
            MachineType: item.machineType,
         })
         .then((res) => {
            if (res?.data?.alreadyExists) {
               setMessage({
                  flavor: "alert-danger",
                  text: `This machine type already exists on item number ${copyThis.itemNumber}. Please cancel and go to that item number to make changes.`,
               });
            } else {
               let machineDefault = _.find(machineTypes, (mt) => {
                  return mt.id === item.machineType;
               });
               let changes = _.assign({}, emptyNewItem);
               changes.itemNumber = copyThis.itemNumber;
               changes.calcType = item.calcType;
               changes.machineType = item.machineType;
               changes = setExtendedProperties(item, changes);
               changes.setupId = 0;
               // setting defaults as initial values now
               changes.stdEfficiency = machineDefault.defaultEfficiency;
               changes.stdSpindleCount = machineDefault.defaultSpindles;
               delete changes.copyToItem;
               saveMachineSetup(changes, true, copyThis); // true says to open this item after refreshing the data
            }
         })
         .catch(api.catchHandler)
         .finally(() => setCopying(false));
   }

   function deleteSetup(item) {
      setSaving(true);
      api
         .fetch(`MachineSetup/DeleteMachineSetup`, {
            SetupId: item.setupId,
            ItemNumber: item.itemNumber,
         })
         .then((res) => {
            if (res && res.data && res.data.success) {
               setMessage(null);
               fetchData(false);
               setItemToDelete(null);
               toast.success("Machine Setup successfully deleted");
            } else {
               setMessage({
                  flavor: "alert-danger",
                  text: res?.data?.message,
               });
            }
         })
         .catch(api.catchHandler)
         .finally(() => setSaving(false));
   }

   function fetchData(openMachineTypeIdAfterRefresh = false) {
      if (loading) return;
      setLoading(true);
      api
         .post(`Quality/GetSpecificationByItemNumber`, {
            ItemNumber: selectedItem.value,
            IncludeMachineSetups: true,
         })
         .then((res) => {
            if (res) {
               let item = res?.data?.item;
               item.value = item.itemNumber;
               item.label = item.ddlText;
               item.machineSetupList = _.map(item.machineSetupList, (l) => {
                  l = setExtendedProperties(l, l); // same item here, but in copying we will pass different item to first parameter
                  l.editing = openMachineTypeIdAfterRefresh
                     ? openMachineTypeIdAfterRefresh === l.machineType
                     : false;
                  return l;
               });
               setSpecInfo(item);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoading(false));
   }

   function onChange(field, value, index) {
      let newList = specInfo.machineSetupList.slice();
      let changes = _.assign({}, newList[index]);
      changes[field] = value;
      newList[index] = changes;
      let newInfo = _.assign({}, specInfo);
      newInfo.machineSetupList = newList;
      setSpecInfo(newInfo);
   }

   function onPrint(index) {
      let newList = specInfo.machineSetupList.slice();
      let itemToPrint = _.assign({}, newList[index]);
      if (!downloading) {
         setDownloading(true);
         let payload = {
            ItemNumber: itemToPrint.itemNumber,
         };
         payload.PrintRevisionNotes = printRevisionNotes || false;
         payload.PrintConfigFields = printConfiguration || false;
         payload.SetUpId = _.isNil(itemToPrint.setupId)
            ? ""
            : _.toString(itemToPrint.setupId);

         api
            .postDownloadBinary(
               "Report/MachineSetupItem",
               payload,
               constants.MIME_TYPES.pdf,
               `MSR_${itemToPrint.itemNumber
               }_${dateHelpers.getDateStringForReportName()}.pdf`,
               "There was an error downloading the machine setup report"
            )
            .then((res) => {
               if (res?.data === false) {
                  toast.error(res.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setDownloading(false));
      }
   }

   function saveMachineSetup(
      item,
      openItemAfterRefresh = false,
      setNewItemToSelected = null
   ) {
      let payload = {};
      payload.SetupId = item.setupId || 0;
      openItemAfterRefresh = openItemAfterRefresh || payload.SetupId === 0;
      let warnings = [];
      if (
         !item.calcType ||
         item.calcType === constants.CALCULATION_TYPES.UNKNOWN
      ) {
         warnings.push("Please select a calculation type\n");
      } else {
         payload.CalcType = item.calcType;
      }
      if (!item.machineType) {
         warnings.push("Please select a machine type\n");
      } else {
         payload.MachineType = item.machineType;
      }
      if (item.calcType === constants.CALCULATION_TYPES.CUMULATIVE.value) {
         if (
            rgx.validateIntegerRange(item.orderToProcess, "orderToProcess", 0, 10)
         ) {
            payload.OrderToProcess = item.orderToProcess;
         } else {
            warnings.push("Order To Process must be 0 to 10.\n");
         }
      }
      payload.Instructions = item.instructions;
      payload.IsActive = item.isActive;
      payload.ItemNumber = item.itemNumber || selectedItem.value;
      payload.SetupId = item.setupId || 0;

      // since each machine type has different extra fields
      // need to add each of those to a dictionary to
      // return to api
      let { newPayload, moreWarnings } = shapeExtraFields(payload, item);

      warnings = _.concat(warnings, moreWarnings);
      if (warnings.length > 0) {
         toast.warning(_.uniq(warnings).join(" "));
         return;
      }
      payload = _.merge(payload, newPayload);
      // setSaving(true);
      // let url = setNewItemToSelected ? 'MachineSetup/CopyMachineSetup' : `MachineSetup/SaveMachineSetup`;
      // api
      //   .post(url, payload)
      //   .then((res) => {
      //     if (res.data.success) {
      //       toast.success("Saved successfully");
      //       fetchData(openItemAfterRefresh ? payload.MachineType : null);
      //       setItemToCopy(null);
      //       if (setNewItemToSelected) {
      //         setSelectedItem(setNewItemToSelected)
      //       }
      //     } else if (res.data.message) {
      //       toast.warning(res.data.message);
      //     }
      //   })
      //   .catch(api.catchHandler)
      //   .finally(() => setSaving(false));
   }

   function setExtendedProperties(item, editItem) {
      _.mapKeys(item.extendedPropertyData, (value, key) => {
         editItem[key] = formatValues(key, value, item.machineType);
      });
      editItem.copyToItem = _.find(copyToItemOptions, (opt) => {
         return opt.itemNumber === item.itemNumber;
      });
      return editItem;
   }

   function toggleActive(item) {
      setSaving(true);
      api
         .fetch(`MachineSetup/ToggleActiveMachineSetup`, {
            SetupId: item.setupId,
            ItemNumber: item.itemNumber,
         })
         .then((res) => {
            fetchData(false);
         })
         .catch(api.catchHandler)
         .finally(() => setSaving(false));
   }

   if (
      specInfo?.machineSetupList?.count <= 1 ||
      _.some(specInfo?.machineSetupList, (l) => {
         return l.calcType === soleSetup.value;
      })
   ) {
      calculationOptions.push(soleSetup);
      calculationOptions.push(cumulativeSetup);
   } else {
      calculationOptions.push(constants.CALCULATION_TYPES.ALTERNATE);
      calculationOptions.push(cumulativeSetup);
   }

   calculationOptions = _.sortBy(calculationOptions, (c) => { return c.seq; });

   const showProcessStep =
      specInfo &&
      _.some(specInfo.machineSetupList, (ms) => {
         return ms.orderToProcess > 0 && ms.calcType === cumulativeSetup.value;
      });

   const showOrderToProcess = editItem && editItem.calcType === cumulativeSetup.value;

   return (
      <DesktopContainer screenName="Machine Setups">
         <div className="filter-header mb-3">
            <Row className="justify-content-center pe-3 text-center">
               <Col xs={12}>
                  <Row className="align-items-center my-1">
                     <Col xs={3}>
                        <h5 children="Item:" className="float-end text-white" />
                     </Col>
                     <Col xs={9} className="text-start">
                        <Select
                           onChange={(e) => setSelectedItem(e)}
                           value={selectedItem}
                           options={itemOptions}
                           makeAnimated={makeAnimated}
                           isClearable
                           classNamePrefix="serviceThreadSelect"
                        />
                     </Col>
                  </Row>
               </Col>
            </Row>
         </div>
         {selectedItem ? (
            <Card className="dasboard-card">
               <CardBody>
                  <Row className="align-items-center my-1">
                     <Col xs={12}>
                        <h5 className="float-start">Quality Machine Setup</h5>
                     </Col>
                  </Row>
                  <hr />
                  <div className="d-flex justify-content-between align-items-center mb-3">
                     <div>
                        <Button
                           className="shredButton mb-2"
                           style={{ marginRight: "1vw" }}
                           children="Add New Setup"
                           disabled={loading || saving}
                           onClick={addNewSetup}
                        />
                        <Button
                           className="shredButton mb-2 px-2"
                           children="Active Machine Setups Report"
                           disabled={loading || saving}
                           onClick={() => onPrint(0)}
                        />
                     </div>
                     <div className="d-flex justify-content-between">
                        <div style={{ marginRight: "5vw" }}>
                           <Input
                              type="checkbox"
                              className="my-2 cursor-pointer"
                              checked={printRevisionNotes}
                              onChange={() => setPrintRevisionNotes(!printRevisionNotes)}
                           ></Input>
                           <Label className="pt-1 ms-3 boldedWriting">
                              Print Revision Notes?
                           </Label>
                        </div>
                        <div style={{ marginRight: "5vw" }}>
                           <Input
                              type="checkbox"
                              className="my-2 cursor-pointer"
                              checked={printConfiguration}
                              onChange={() => setPrintConfiguration(!printConfiguration)}
                           ></Input>
                           <Label className="pt-1 ms-3 boldedWriting">
                              Print Configuration?
                           </Label>
                        </div>
                     </div>
                  </div>
                  {specInfo &&
                     specInfo.machineSetupList &&
                     specInfo.machineSetupList.length > 0 ? (
                     <>
                        <Row className="align-items-center my-1">
                           <Col xs={12}>
                              <Table size="sm" className="data-table">
                                 <thead className="table-header">
                                    <tr>
                                       <th style={{ width: "10%" }}>Calc Method</th>
                                       {showProcessStep && (
                                          <th style={{ width: "10%" }}>Process Step</th>
                                       )}
                                       <th style={{ width: "20%" }}>Machine Type</th>
                                       <th />
                                       <th style={{ width: "20%" }} />
                                       <th />
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {_.map(specInfo.machineSetupList, (ms, index) => {
                                       return (
                                          <tr
                                             key={`machineSetup-${index}-${ms.setupId}`}
                                             className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                                          >
                                             <td className="data-cell">{ms.calcTypeText}</td>
                                             {showProcessStep && (
                                                <td className="data-cell text-center">
                                                   {ms.calcType === constants.CALCULATION_TYPES.CUMULATIVE.value
                                                      ? ms.orderToProcess
                                                      : null}
                                                </td>
                                             )}
                                             <td className="data-cell" children={ms.machineDescription} />
                                             <td className="data-cell">
                                                <ButtonGroup className="btn-grp-sm float-start">
                                                   <Button
                                                      onClick={() => onPrint(index)}
                                                      className="fontSize85 projectSecondary"
                                                   >
                                                      <GrPrint className="whiteIcon me-2" color="#fff" />{" "}
                                                      Print
                                                   </Button>
                                                   {canEdit && (
                                                      <Button
                                                         onClick={() =>
                                                            onChange(
                                                               "editing",
                                                               !ms.editing, //flip the value on/off
                                                               index
                                                            )
                                                         }
                                                         children={
                                                            <>
                                                               <FaEdit className="whiteIcon me-2" />{" "}
                                                               Edit
                                                            </>
                                                         }
                                                         disabled={saving || !ms.isActive}
                                                         className="projectPrimary fontSize85"
                                                      />
                                                   )}
                                                </ButtonGroup>
                                             </td>
                                             <td className="data-cell">
                                                {canEdit && (
                                                   <InputGroup className="copy-to-item-input-group">
                                                      <div className="copy-to-item-select-wrapper">
                                                         <Select
                                                            onChange={(e) => onChange("copyToItem", e, index)}
                                                            value={ms.copyToItem}
                                                            options={copyToItemOptions}
                                                            makeAnimated={makeAnimated}
                                                            className="width115px copy-to-item"
                                                            classNamePrefix="serviceThreadSelect"
                                                         />
                                                      </div>
                                                      <Button
                                                         onClick={() => {
                                                            if (ms.copyToItem?.itemNumber === selectedItem.itemNumber) {
                                                               toast.warning("Cannot copy this machine setup to the same item");
                                                            } else {
                                                               setItemToCopy(ms);
                                                            }
                                                         }}
                                                         children="Copy"
                                                         disabled={!ms.copyToItem}
                                                         className="shredButton fontSize85 copy-to-item"
                                                      />
                                                   </InputGroup>
                                                )}
                                             </td>
                                             <td className="data-cell">
                                                <ButtonGroup className="btn-grp-sm float-start">
                                                   {canActivate && (
                                                      <Button
                                                         onClick={() => toggleActive(ms)}
                                                         children={
                                                            <>
                                                               {ms.isActive ? (
                                                                  <FaTimesCircle className="me-2" />
                                                               ) : (
                                                                  <FaRecycle className="me-2" />
                                                               )}
                                                               {ms.isActive
                                                                  ? "Deactivate"
                                                                  : "Activate"}
                                                            </>
                                                         }
                                                         disabled={saving}
                                                         className={
                                                            ms.isActive
                                                               ? "projectWarning fontSize85 width115px"
                                                               : "projectInfo fontSize85 width115px"
                                                         }
                                                      />
                                                   )}
                                                   {canDelete && (
                                                      <Button
                                                         onClick={() => setItemToDelete(ms)}
                                                         children={
                                                            <>
                                                               <FaTimesCircle className="me-2" />
                                                               Delete
                                                            </>
                                                         }
                                                         disabled={saving}
                                                         className="projectDanger fontSize85"
                                                      />
                                                   )}
                                                </ButtonGroup>
                                             </td>
                                          </tr>
                                       );
                                    })}
                                 </tbody>
                                 <tfoot className="table-footer">
                                    <tr>
                                       <td colSpan={showProcessStep ? "6" : "5"} />
                                    </tr>
                                 </tfoot>
                              </Table>
                           </Col>
                        </Row>
                     </>
                  ) : null}
               </CardBody>
            </Card>
         ) : null}

         {editItem && (
            <>
               <Card className=" dashboard-card mb-2">
                  <CardBody className="pb-2">
                     <Row className="mb-1">
                        <Col xs={3}>
                           <span children="Item:" className="float-end boldedWriting" />
                        </Col>
                        <Col xs={6}>
                           <Select
                              value={selectedItem}
                              classNamePrefix="serviceThreadSelect"
                              isDisabled
                           />
                        </Col>
                     </Row>
                     <Row className="mb-1">
                        <Col xs={3}>
                           <span children="Machine Type:" className="float-end boldedWriting" />
                        </Col>
                        <Col xs={6}>
                           <Select
                              value={_.find(
                                 machineTypes,
                                 (i) => i.value === editItem.machineType
                              )}
                              onChange={(e) => onChange("machineType", e.value, editItemIndex)}
                              options={machineTypes}
                              makeAnimated={makeAnimated}
                              classNamePrefix="serviceThreadSelect"
                           />
                        </Col>
                     </Row>
                     <Row className="mb-1">
                        <Col xs={3}>
                           <span children="Labor Calculation Type:" className="float-end boldedWriting" />
                        </Col>
                        <Col xs={showOrderToProcess ? 3 : 6}>
                           <Select
                              value={_.find(
                                 calculationOptions,
                                 (i) => i.value === editItem.calcType
                              )}
                              onChange={(e) => onChange("calcType", e.value, editItemIndex)}
                              options={calculationOptions}
                              makeAnimated={makeAnimated}
                              classNamePrefix="serviceThreadSelect"
                           />
                        </Col>
                        {showOrderToProcess && (
                           <>
                              <Col xs={3}>
                                 <h5 children="Order To Process:" className="float-end" />
                              </Col>
                              <Col xs={3}>
                                 <DebounceInput
                                    value={editItem?.orderToProcess}
                                    onChange={(e) => onChange("orderToProcess", e.target.value, editItemIndex)}
                                    debounceTimeout={300}
                                    className="w-75 form-control"
                                 />
                              </Col>
                           </>
                        )}
                     </Row>
                     <Row className="mb-3 mt-2">
                        <div>
                           <span className="fontSize85" style={{ display: "table", margin: "0 auto" }}>
                              NOTE: Any change to the labor calculation type on this setup
                              will cause all other setups for this item to be changed as well.
                           </span>
                        </div>
                     </Row>

                     <hr />
                     <MachineSetupArea
                        {...editItem}
                        editItem={editItem}
                        index={editItemIndex}
                        onChange={onChange}
                        specInfo={specInfo}
                        setSpecInfo={setSpecInfo}
                     />
                     <Row className="mb-1">
                        <Col xs={3} className="pt-2">
                           <span children="Special Instructions:" className="float-end boldedWriting" />
                        </Col>
                        <Col xs={9}>
                           <DebounceInput
                              className="serviceThreadTextArea w-75"
                              element="textarea"
                              value={editItem?.instructions}
                              onChange={(e) => onChange("instructions", e.target.value, editItemIndex)}
                              debounceTimeout={300}
                           />
                        </Col>
                     </Row>
                  </CardBody>
                  <CardBody
                     className="pb-1"
                     style={{
                        borderTopStyle: "solid",
                        borderTopWidth: 1,
                        borderTopColor: "rgba(0, 0, 0, 0.125)",
                     }}
                  >
                     <Button
                        className="shredButton float-start mb-2"
                        children="Save Machine Setup"
                        disabled={loading || saving}
                        onClick={() => saveMachineSetup(editItem)}
                     />
                  </CardBody>
               </Card>
               <Card className="mb-2">
                  <CardBody>
                     <Row className="align-items-center mb-2">
                        <Col xs={12}>
                           <h5 className="float-start">Revision History:</h5>
                        </Col>
                     </Row>
                     <hr />
                     {editItem &&
                        editItem.setupRevisionNotes &&
                        editItem.setupRevisionNotes.length > 0 ? (
                        <Table>
                           <thead className="table-header">
                              <tr>
                                 <th width="10%">Revision #</th>
                                 <th width="15%">Date</th>
                                 <th>Note</th>
                              </tr>
                           </thead>
                           <tbody>
                              {_.map(
                                 _.sortBy(editItem.setupRevisionNotes, (n) => { return n.revNumber; }),
                                 (n, idx) => {
                                    return (
                                       <tr key={`revNote${idx}${n.revNumber}`}>
                                          <td className="data-cell">{n.revisionNumber}</td>
                                          <td className="data-cell">{dateHelpers.toLongDateString(n.revisionDate)}</td>
                                          <td className="data-cell">{n.note}</td>
                                       </tr>
                                    );
                                 }
                              )}
                           </tbody>
                        </Table>
                     ) : null}
                  </CardBody>
               </Card>

               <Card>
                  <CardBody>
                     <AttachmentManager
                        attachmentTypeId={constants.ATTACHMENT_TYPES.MachineSetup}
                        contextName={editItem.setupId}
                        attachmentKey={`Machine Setup ${editItem.setupId}`}
                     />
                  </CardBody>
               </Card>
            </>
         )}
         <ConfirmModal
            confirmDelete={itemToDelete}
            approveConfirmDelete={() => {
               deleteSetup(itemToDelete);
            }}
            declineConfirmDelete={() => {
               setItemToDelete(null);
               setMessage(null);
            }}
            errorMessage={message}
            disabled={saving}
            warningMessage={
               <>
                  Are you sure you want to delete this machine setup?
                  <br />
                  This will permanently remove it from the system.
               </>
            }
         />
         <ConfirmModal
            confirmDelete={itemToCopy}
            approveConfirmDelete={() => {
               copyItemSetup(itemToCopy);
            }}
            declineConfirmDelete={() => {
               setItemToCopy(null);
               setMessage(null);
            }}
            errorMessage={message}
            disabled={saving || copying}
            warningMessage={
               <>Are you sure you want to make a copy of this machine setup?</>
            }
         />
      </DesktopContainer>
   );
}
